import { useState, useEffect, useRef, useCallback, useMemo, startTransition } from "react";
import { AGENT_FILL_EVENT, agentSelectionTimes, readAgentSelection } from "@/lib/agent-prefill";
import { Shield, MapPin, Pencil, Calendar, ChevronDown, ChevronLeft, X, Plane, Users, MessageCircle, Info, Zap, Gem, TrendingUp, CalendarClock, Check } from "lucide-react";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Spinner } from "@/components/ui/spinner";
import { RollingNumber, useReducedMotion } from "@/components/ui/rolling-number";
import { useLocation } from "wouter";
import { cn, formatDate, isValidIsoDate } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { useUser } from "@/hooks/useUser";
import { useFeatureFlag } from "@/hooks/useFeatureFlags";
import { Sentry } from "@/lib/sentry";
import { bookingsApi, type PriceQuote, QuoteApiError } from "@/lib/bookings-api";
import { useTranslation } from "react-i18next";
import { bookingLocationCopy } from "@/lib/booking-location-copy";
import { getCurrencySymbol } from "@workspace/api-client-react";
import { useCurrency } from "@/i18n/CurrencyContext";
import { useLocale } from "@/i18n/LocaleContext";
import { formatChargeDisclosure, useFormatMoney } from "@/lib/formatMoney";
import type { CarData, DeliveryAirport, AvailabilityData, ScarcitySignal } from "@/lib/api";
import { matchDeliveryAirport } from "@/lib/airport-match";
import { buildCarPath, getDistanceUnit, getDistanceUnitPlural, isMetricCountry } from "@/lib/api";
import type { RentalKind } from "@/lib/users-api";
import { DsIcon, type DsIconName } from "@/components/ui/ds-icons";
import { InsuranceModal } from "./InsuranceModal";
import { PriceBreakdownLines } from "@/components/booking/PriceBreakdownLines";
import { buildPriceBreakdownLines, isUsPickupDeadlineError, usPickupDeadlineMessage } from "@workspace/booking-rules";
import { TimePicker } from "@/components/ui/time-picker";
import { useAuthModal } from "@/components/auth/AuthModalContext";
import { ALL_OCCASIONS } from "@/lib/occasions";
import { Select, SelectContent, SelectItem, SelectValue, SelectTrigger } from "@workspace/ui/components/ui/select";
import { Label } from "@workspace/ui/components/ui/label";
import { Drawer, DrawerContent, DrawerTitle } from "@workspace/ui/components/ui/drawer";
import { Popover, PopoverAnchor, PopoverContent } from "@workspace/ui/components/ui/popover";
import { AvailabilityCalendar } from "@/components/cars/AvailabilityCalendar";
import { DateRangePopup } from "@/components/cars/DateRangePopup";
import { MobileAwareAutocomplete } from "@/components/search/MobileAwareAutocomplete";
import { addressSuggestionLines, optionalString } from "@/lib/address-suggestion-label";
import { useIsNarrow } from "@/hooks/use-mobile";
import { deriveCarTimezone } from "@/lib/car-timezone";
import { getDefaultTimes } from "@/lib/trip-datetime";
import { createCheckoutFlowId, normalizeCheckoutFlowId, readSecureCheckoutState, canClaimCheckoutFlowAfterAuth, rememberCheckoutFlowForPostAuthClaim, writeSecureCheckoutState } from "@/lib/checkout-state";
import { coverageConfirmationContext, eligiblePlatformCoverage } from "@/lib/checkout-insurance";
import { buildDateRangeRejectionProps } from "@/lib/analytics/checkout-diagnostics";
import { markCheckoutRouteEntry } from "@/lib/checkout-route-signal";
import { hasTimedBookingConflict } from "@/lib/timed-availability";
import { parseBookingTime } from "@/lib/booking-time";
import { checkoutTimeIssue, checkoutSelectionProperties } from "@/lib/checkout-time-selection";
import { HourlyTripFields } from "@/components/cars/HourlyTripFields";
import { EventTripFields } from "@/components/cars/EventTripFields";
import { minimumHoursFor, DEFAULT_HOURLY_START, FULL_DAY_HOURS, deriveHourlyEnd, elapsedHours, formatClock, hourlyDurationOptions, pickRestoredDuration, usesTwelveHourClock } from "@/lib/hourly-booking";
import { serviceModeFor, trackBookingBox } from "@/lib/booking-widget-tracking";
type EventTypeRef = { id?: number; name?: string; slug: string };

const BW_API_BASE = `${import.meta.env.BASE_URL || "/"}api`;


interface BookingWidgetProps {
  checkoutFlowId?: string;
  car: CarData;
  slug: string;
  isPreview?: boolean;
  dateFrom?: string;
  dateTo?: string;
  onDateFieldClick?: () => void;
  deliveryAirports?: DeliveryAirport[];
  onRentalTypeChange?: (type: RentalTab) => void;
  ownerResponseRate?: number | null;
  owner?: {
    id?: string | null;
    first_name: string | null;
    last_name?: string | null;
    profile_image: string | null;
    city?: string | null;
  } | null;
  primaryImageUrl?: string | null;
  /**
   * Legacy: host-enabled event-type slugs/names for this listing. Sourced
   * from the top-level CarDetail payload (data.eventTypes), not from the
   * car row itself. Originally fed the optional Occasion picker on the
   * Event rental tab, but the per-listing event_types config was removed
   * from the listing wizard, so this is effectively always empty today
   * and the picker below never renders. The occasion is now collected on
   * the Confirm-and-pay page instead. Kept for back-compat with any data
   * still flowing through the CarDetail payload.
   */
  eventTypes?: EventTypeRef[];
  /**
   * Tunes the layout for the mobile bottom-sheet drawer:
   * larger touch targets, vertical scroll area, and a primary
   * CTA pinned to the bottom with safe-area padding.
   */
  mobile?: boolean;
  /**
   * Mobile-bug fix #1: when both `availability` and `onDateSelect` are
   * supplied AND the widget is rendered in `mobile` mode, tapping the
   * pick-up / drop-off date fields opens an inline calendar Drawer instead
   * of falling back to `onDateFieldClick` (which on mobile would scroll to
   * the inline AvailabilityCalendar that's hidden behind the bottom-sheet).
   * Desktop never uses this path — `onDateFieldClick` keeps doing the
   * scroll-to-anchor it always did. Kept optional so existing call-sites
   * (preview iframe, mockup sandbox) that don't have availability data
   * continue to work.
   */
  availability?: AvailabilityData | null;
  onDateSelect?: (from: string, to: string) => void;
  /**
   * Initial selections forwarded from the car-detail URL query string.
   * Used to restore the renter's prior choices when they hit Back from
   * the Confirm-and-pay page (task #1699). Only applied on first mount —
   * later URL changes do not reset the widget mid-session.
   */
  initialDateFrom?: string;
  initialDateTo?: string;
  initialRentalType?: RentalTab;
  initialPickupTime?: string;
  initialDropoffTime?: string;
  initialNumPassengers?: number;
  initialOccasion?: string;
  initialAirportCode?: string | null;
  initialDeliveryDistance?: number | null;
  initialDeliveryAddress?: string;
  /**
   * Renter-typed message to the host. Captured on the Confirm-and-pay
   * page; round-tripped through the URL so that if the renter hits Back
   * and then Reserve again, their message comes along instead of
   * silently disappearing (#1699).
   */
  initialMessage?: string;
  /**
   * Task #6806 — at most one real-data scarcity signal for this car,
   * resolved server-side (priority rarity > demand > availability) and
   * gated by the `feature_scarcity_banner` flag. Null/absent = no banner.
   */
  scarcity?: ScarcitySignal | null;
  /** Mobile drawer only: closes the hosting reserve drawer from the step header. */
  onMobileClose?: () => void;
}

// ── Session storage helpers ────────────────────────────────────────────────
// Persist the renter's widget inputs for the duration of the browser tab so
// returning to the car detail page restores what they entered without
// requiring them to re-fill dates, times, and delivery location.

interface BwSession {
  insurancePackageId?: number | null;
  dateFrom?: string;
  dateTo?: string;
  pickupTime?: string;
  dropoffTime?: string;
  activeTab?: RentalTab;
  deliveryAddress?: string;
  deliveryAirportCode?: string | null;
  // Persisted so a same-tab refresh restores the resolved delivery
  // distance alongside the address; without it the quote re-fetched on
  // mount would silently omit the delivery fee while still showing the
  // address (Task #4227).
  deliveryDistance?: number | null;
}

function bwSessionKey(carId: number | string): string {
  return `bac:bw:${carId}`;
}

function readBwSession(carId: number | string): BwSession {
  try {
    const raw = typeof sessionStorage !== "undefined" ? sessionStorage.getItem(bwSessionKey(carId)) : null;
    if (!raw) return {};
    return JSON.parse(raw) as BwSession;
  } catch {
    return {};
  }
}

function writeBwSession(carId: number | string, data: BwSession): void {
  try {
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.setItem(bwSessionKey(carId), JSON.stringify(data));
    }
  } catch {
    // best-effort; quota errors should not break the widget
  }
}

function clearBwSession(carId: number | string): void {
  try {
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.removeItem(bwSessionKey(carId));
    }
  } catch {
    // best-effort
  }
}
// ──────────────────────────────────────────────────────────────────────────

function haversineDistance(lat1: number, lon1: number, lat2: number, lon2: number, useKm: boolean = false): number {
  const R = useKm ? 6371.0 : 3958.8;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

type RentalTab = RentalKind;

function computeIsInsurable(car: CarData, tab: RentalKind): boolean {
  return eligiblePlatformCoverage(car.country_code, car.state, car.insured_value, tab);
}

function formatDisplayDate(dateStr: string): string {
  if (!dateStr) return "";
  return formatDate(dateStr, { month: "2-digit", day: "2-digit", year: "2-digit" });
}





export function BookingWidget({ checkoutFlowId, car, slug, isPreview = false, dateFrom: externalFrom, dateTo: externalTo, onDateFieldClick, deliveryAirports = [], onRentalTypeChange, ownerResponseRate, owner = null, primaryImageUrl = null, eventTypes = [], mobile = false, availability = null, onDateSelect, initialDateFrom, initialDateTo, initialRentalType, initialPickupTime, initialDropoffTime, initialNumPassengers, initialOccasion, initialAirportCode, initialDeliveryDistance, initialDeliveryAddress, initialMessage, scarcity = null, onMobileClose }: BookingWidgetProps) {
  const { t, i18n } = useTranslation();
  const { localizedHref, market } = useLocale();
  const { session, user: authenticatedUser, isLoading: authLoading } = useUser();
  const user = authenticatedUser?.app_metadata?.checkout_guest === true ? null : authenticatedUser;
  const { toast } = useToast();
  const isNarrow = useIsNarrow();
  // Task #4594 — global Instant Book kill-switch. When OFF, the booking CTA
  // always reads "request to book" regardless of the car's stored
  // `instant_rental`, matching the server (which forces request-to-book and
  // returns quote.instantBooking=false while this flag is off).
  const instantBookEnabled = useFeatureFlag("feature_instant_book");
  const [, navigate] = useLocation();
  const authModal = useAuthModal();
  const checkoutFlowIdRef = useRef(
    checkoutFlowId || (typeof window !== "undefined"
      ? normalizeCheckoutFlowId(new URLSearchParams(window.location.search).get("h"))
      : createCheckoutFlowId()),
  );
  const retainListingFlow = () => {
    if (isPreview || typeof window === "undefined") return;
    // Native browser Back and a listing refresh must resume this flow too,
    // not just checkout's explicit back link. Only the opaque id is added.
    const url = new URL(window.location.href);
    url.searchParams.set("h", checkoutFlowIdRef.current);
    window.history.replaceState(window.history.state, "", url);
  };
  const currency = car.currency_code || "USD";
  // Task #2342 — browsing surfaces (car detail) render prices in the
  // renter's preferred currency only. The legacy `≈ {converted}` line
  // is gone; if no FX rate is available we fall back to the listing's
  // local currency so the widget still shows a real number.
  const { formatPreferred, currency: preferredCurrency, formatChargeDisclosure: chargeDisclosure } = useCurrency();
  const { money: formatLocalisedMoney } = useFormatMoney();
  const maskPrice = (value: number | null | undefined, cur: string) =>
    isPreview ? "xxx" : (formatPreferred(value ?? 0, cur) ?? formatLocalisedMoney(value ?? 0, cur));

  const tabIconMap: Record<RentalTab, DsIconName> = {
    driving: "driveable",
    chauffeured: "chauffeured",
    event: "event-rental",
  };

  const isUSListing = car.country_code?.toUpperCase() === "US";
  const isNewYorkListing = ["NY", "NEW YORK"].includes(car.state?.trim().toUpperCase() || "");
  const isUnverifiedUSListing = isUSListing && !car.state?.trim();
  const selfDriveUnavailable = isNewYorkListing || isUnverifiedUSListing;
  const allTabs: { key: RentalTab; label: string; active: boolean; price: number | null; unit: string }[] = [
    { key: "driving", label: t("common.rental_types.self_drive"), active: !!car.rental_driving_active && !selfDriveUnavailable, price: car.price_driving, unit: t("common.per_day") },
    { key: "chauffeured", label: t("common.rental_types.chauffeur"), active: !!car.rental_chauffeured_active, price: car.chauffeured_hourly_rate, unit: t("common.per_hour") },
    { key: "event", label: t("common.rental_types.events"), active: !!car.rental_event_active, price: car.event_hourly_rate, unit: t("common.per_hour") },
  ];
  const tabs = allTabs.filter(t => t.active);

  // Match the public Offer selector: an active but nonpositive/unknown rate
  // must not conceal another active, positively priced offer on first paint.
  const defaultTab = tabs.find(tab => Number.isFinite(Number(tab.price)) && Number(tab.price) > 0)?.key ||
    tabs[0]?.key || "driving";

  // Read the saved session once on mount (lazy initializer so it only runs
  // once even if the component re-renders before state settles).
  const [sess] = useState<BwSession>(() => isPreview ? {} : readBwSession(car.id));

  // Honour an initial rental type from the URL when it's actually one of
  // the host-enabled tabs; otherwise check session storage, then fall back
  // to the default tab so we don't try to render an inactive tab when the
  // renter follows a stale back-link with `?type=event` against a now-
  // driving-only listing.
  const initialTab: RentalTab =
    (initialRentalType && tabs.some(tt => tt.key === initialRentalType))
      ? initialRentalType
      : (sess.activeTab && tabs.some(tt => tt.key === sess.activeTab))
        ? sess.activeTab
        : defaultTab;
  const [activeTab, setActiveTab] = useState<RentalTab>(initialTab);
  useEffect(() => { onRentalTypeChange?.(initialTab); }, []);
  // Seed order: URL param → session storage → "".
  // URL params (initialDateFrom/initialDateTo) are only populated on
  // checkout back-navigation so they always win over a saved session.
  // Sanitize every restored value: a stale / corrupted / localized
  // sessionStorage entry that isn't a well-formed YYYY-MM-DD must be treated
  // as unset so it can never persist into a quote request (which the server
  // 400s as `Invalid date format`, flooding Sentry).
  const [dateFrom, setDateFrom] = useState(
    isValidIsoDate(initialDateFrom) ? initialDateFrom! : (isValidIsoDate(sess.dateFrom) ? sess.dateFrom! : ""),
  );
  const [dateTo, setDateTo] = useState(
    isValidIsoDate(initialDateTo) ? initialDateTo! : (isValidIsoDate(sess.dateTo) ? sess.dateTo! : ""),
  );
  // Mobile-bug fix #1: in-widget date Drawer for the mobile bottom sheet.
  // Only opens when the parent has supplied availability + a date setter;
  // otherwise we fall back to onDateFieldClick (desktop scroll-to-anchor).
  const [dateDrawerOpen, setDateDrawerOpen] = useState(false);
  const canOpenDateDrawer = !!(mobile && availability && onDateSelect);
  // Task #1086: desktop Airbnb-style date popup. Same gating as the mobile
  // drawer (parent supplied availability + date setter), but only on desktop.
  // When unavailable (e.g. preview iframe / mockup sandbox without
  // availability data), we fall back to the legacy scroll-to-anchor.
  const [datePopupOpen, setDatePopupOpen] = useState(false);
  const [datePopupActiveField, setDatePopupActiveField] = useState<"start" | "end">("start");
  const canOpenDatePopup = !!(!mobile && availability && onDateSelect);
  const openDatePicker = (field: "start" | "end") => {
    if (canOpenDatePopup) {
      // INP: mounting the calendar popover is expensive — yield to the browser
      // first so the tap registers before the heavy render begins.
      startTransition(() => {
        setDatePopupActiveField(field);
        setDatePopupOpen(true);
      });
      return;
    }
    if (canOpenDateDrawer) {
      startTransition(() => { setDateDrawerOpen(true); });
      return;
    }
    onDateFieldClick?.();
  };
  const handlePickupClick = () => openDatePicker("start");
  const handleDropoffClick = () => openDatePicker("end");
  const initialDefaults = getDefaultTimes(initialTab);
  // Seed order: URL param → session storage → tab default.
  const [pickupTime, setPickupTime] = useState(initialPickupTime || sess.pickupTime || (initialTab === "chauffeured" ? DEFAULT_HOURLY_START : initialDefaults.pickup));
  const [dropoffTime, setDropoffTime] = useState(initialDropoffTime || sess.dropoffTime || initialDefaults.dropoff);
  // Chauffeur: Date + Start + Duration. Events have an explicit start/end;
  // these options only seed an unset event end when selecting its first date.
  const carExtra = car as CarData & { chauffeured_min_hours?: number | null; event_min_hours?: number | null };
  const hourlyOptionsFor = (tab: RentalTab) => tab === "chauffeured"
    ? hourlyDurationOptions(minimumHoursFor({ explicitMinHours: carExtra.chauffeured_min_hours, minimumCharge: car.chauffeured_min_price_hourly_rate || car.minimum_price, hourlyRate: car.chauffeured_hourly_rate }))
    : hourlyDurationOptions(minimumHoursFor({ explicitMinHours: carExtra.event_min_hours, minimumCharge: car.event_min_price_hourly_rate || car.minimum_price, hourlyRate: car.event_hourly_rate }));
  const hourlyOptions = useMemo(() => hourlyOptionsFor(activeTab === "driving" ? "chauffeured" : activeTab),
    [activeTab, car.chauffeured_min_price_hourly_rate, car.event_min_price_hourly_rate, car.minimum_price, car.chauffeured_hourly_rate, car.event_hourly_rate, carExtra.chauffeured_min_hours, carExtra.event_min_hours]);
  const [hourlyHours, setHourlyHours] = useState<number>(() => {
    const restoredPickup = initialPickupTime || sess.pickupTime;
    const restoredDropoff = initialDropoffTime || sess.dropoffTime;
    const from = isValidIsoDate(initialDateFrom) ? initialDateFrom! : (isValidIsoDate(sess.dateFrom) ? sess.dateFrom! : "");
    const to = isValidIsoDate(initialDateTo) ? initialDateTo! : (isValidIsoDate(sess.dateTo) ? sess.dateTo! : from);
    const restored = initialTab !== "driving" && restoredPickup && restoredDropoff && from
      ? (() => {
          const direct = elapsedHours(from, restoredPickup, to || from, restoredDropoff);
          // Legacy chauffeur rows keep dateTo = dateFrom; a cross-midnight end wraps.
          return direct ?? elapsedHours(from, restoredPickup, deriveHourlyEnd(from, "12:00 am", 24)!.dateTo, restoredDropoff);
        })()
      : null;
    return pickRestoredDuration(hourlyOptionsFor(initialTab === "driving" ? "chauffeured" : initialTab), restored);
  });
  // Tab switch / car data: keep a still-valid duration, otherwise move to the
  // first offered duration at or above it (never below the car's minimum).
  useEffect(() => {
    if (hourlyOptions.length === 0 || hourlyOptions.includes(hourlyHours)) return;
    setHourlyHours(hourlyOptions.find(h => h >= hourlyHours) ?? hourlyOptions[hourlyOptions.length - 1]);
  }, [hourlyOptions, hourlyHours]);
  // Skip the auto-default reset on first mount so the URL-supplied
  // pickup/dropoff times survive — the renter sees their previous
  // selection instead of the tab default. The effect still fires on
  // subsequent tab switches inside the widget.
  const tabDefaultsAppliedRef = useRef(true);
  useEffect(()=>{
    const fill=(event:Event)=>{
      const detail=(event as CustomEvent).detail;
      if(detail?.carId!==Number(car.id))return;
      if(!mobile && window.matchMedia?.("(max-width: 1023px)").matches)return;
      const selection=readAgentSelection(new URLSearchParams(detail.selection));
      const requested=selection.hire;
      if(!requested || !tabs.some(tab=>tab.key===requested)) {detail.acknowledge?.(false);return;}
      const times=agentSelectionTimes(selection);
      if(requested!=="driving" && (!selection.hours || !times.pickup || !times.dropoff)) {
        detail.acknowledge?.(false);return;
      }
      if(selection.hire!==activeTab)tabDefaultsAppliedRef.current=true;
      setActiveTab(requested);
      if(times.pickup)setPickupTime(times.pickup);
      if(times.dropoff)setDropoffTime(times.dropoff);
      if(requested!=="driving" && selection.hours){
        const wanted=Number(selection.hours);
        const match=hourlyOptions.find(h=>h>=wanted);
        setHourlyHours(match ?? FULL_DAY_HOURS);
      }
      detail.acknowledge?.(true);
      // No click, submit, checkout, auth or payment function is invoked.
    };
    window.addEventListener(AGENT_FILL_EVENT,fill);
    return ()=>window.removeEventListener(AGENT_FILL_EVENT,fill);
  },[car.id,tabs,mobile,activeTab]);
  useEffect(() => {
    if (tabDefaultsAppliedRef.current) {
      tabDefaultsAppliedRef.current = false;
      return;
    }
    const defaults = getDefaultTimes(activeTab);
    // Events have explicit start/end clocks: apply the defaults as a pair.
    // Only chauffeur derives its return from a noon start plus duration.
    setPickupTime(activeTab === "chauffeured" ? DEFAULT_HOURLY_START : defaults.pickup);
    setDropoffTime(defaults.dropoff);
  }, [activeTab]);
  const [quote, setQuote] = useState<PriceQuote | null>(null);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const [cutoffRejected, setCutoffRejected] = useState(false);
  const [serverTimeError, setServerTimeError] = useState<{ reason: string; message: string } | null>(null);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const reducedMotion = useReducedMotion();
  const [booking, setBooking] = useState(false);
  const [numPassengers, setNumPassengers] = useState(initialNumPassengers && initialNumPassengers > 0 ? initialNumPassengers : 1);
  const [selectedInsurancePackageId, setSelectedInsurancePackageId] = useState<number | null>(() =>
    readSecureCheckoutState(checkoutFlowIdRef.current, user?.id,
      !user?.id || canClaimCheckoutFlowAfterAuth(checkoutFlowIdRef.current)).insurancePackageId ??
    (!mobile ? 2 : sess.insurancePackageId === null ? null
      : typeof sess.insurancePackageId === "number" && Number.isSafeInteger(sess.insurancePackageId) && sess.insurancePackageId > 0
        ? sess.insurancePackageId : 2),
  );
  const [insuranceModalOpen, setInsuranceModalOpen] = useState(false);
  const [widgetConfirmation, setWidgetConfirmation] = useState(() =>
    readSecureCheckoutState(checkoutFlowIdRef.current, user?.id,
      !user?.id || canClaimCheckoutFlowAfterAuth(checkoutFlowIdRef.current)).coverageConfirmation);
  const widgetOwnerRef = useRef(user?.id);
  useEffect(() => {
    if (widgetOwnerRef.current === user?.id) return;
    const canClaim = !widgetOwnerRef.current && canClaimCheckoutFlowAfterAuth(checkoutFlowIdRef.current);
    widgetOwnerRef.current = user?.id;
    if (canClaim) return;
    // Initial auth hydration is not a new listing visit. Keep the shared
    // desktop/drawer flow id, but read only data belonging to this owner.
    const saved = readSecureCheckoutState(checkoutFlowIdRef.current, user?.id, false);
    setWidgetConfirmation(saved.coverageConfirmation);
    setSelectedInsurancePackageId(saved.insurancePackageId ?? 2);
    setInsuranceModalOpen(false);
  }, [user?.id]);
  useEffect(() => {
    const syncCoverage = () => {
      const saved = readSecureCheckoutState(checkoutFlowIdRef.current, user?.id);
      setWidgetConfirmation(saved.coverageConfirmation);
      if (saved.insurancePackageId != null) setSelectedInsurancePackageId(saved.insurancePackageId);
    };
    window.addEventListener("checkout-coverage-committed", syncCoverage);
    return () => window.removeEventListener("checkout-coverage-committed", syncCoverage);
  }, [user?.id]);
  // Legacy occasion-picker state. The picker below is gated on the host
  // having enabled per-listing event types, but that wizard step was
  // removed, so in practice this state stays as the empty default and
  // the renter chooses an occasion on the Confirm-and-pay page instead.
  // Empty string = "no preference" (no param sent).
  const [occasion, setOccasion] = useState<string>(initialOccasion || "");
  const quoteAbortRef = useRef<AbortController | null>(null);
  // Task #7234 — latch so `date_range_rejected(time_range_invalid)` fires
  // once per distinct invalid selection, not on every re-render.
  const invalidTimeStartedAtRef = useRef<number | null>(null);
  const emitFunnel = useCallback((event: "checkout_cta_clicked" | "checkout_cta_outcome" | "checkout_time_validation", props: Record<string, unknown>) => {
    void import("@/lib/analytics").then(({ track }) => track(event, props as never)).catch(() => {});
  }, []);

  const [deliveryEditing, setDeliveryEditing] = useState(false);
  // Pre-fill the complete delivery selection when the renter returns from
  // checkout. The secure checkout state carries the human-readable address,
  // distance, and airport code together; session data is only a fallback.
  const initialDeliveryAirport = (() => {
    if (!initialAirportCode) return null;
    const match = deliveryAirports.find(a => a.airport_code === initialAirportCode);
    return match || null;
  })();
  const seededDeliveryAddress =
    initialDeliveryAddress
    || (initialDeliveryAirport ? `${initialDeliveryAirport.airport_name} (${initialDeliveryAirport.airport_code})` : "")
    || (initialDeliveryDistance && initialDeliveryDistance > 0 ? t("booking.your_address", "Your address") : "")
    // Session fallback: only applied when no URL-sourced delivery data exists
    || sess.deliveryAddress
    || "";
  const [deliveryAddress, setDeliveryAddress] = useState(seededDeliveryAddress);
  const [deliveryLatitude, setDeliveryLatitude] = useState<number | null>(null);
  const [deliveryLongitude, setDeliveryLongitude] = useState<number | null>(null);
  const [deliveryInputValue, setDeliveryInputValue] = useState("");
  // Session fallback for airport code when no URL param is present.
  // initialAirportCode is null when absent from the URL (never empty string).
  const seededDeliveryAirportCode =
    initialAirportCode ?? (sess.deliveryAirportCode !== undefined ? sess.deliveryAirportCode : null);
  // Seed order mirrors the address: URL param → session storage. An
  // airport-delivery selection has no road distance, so it forces null.
  // Restoring the distance from session (Task #4227) keeps the re-fetched
  // quote identical to the pre-refresh quote — otherwise the address text
  // would survive a refresh but the distance would reset to null and the
  // server would return a quote with the delivery fee silently dropped.
  const [deliveryDistance, setDeliveryDistance] = useState<number | null>(
    seededDeliveryAirportCode
      ? null
      : (initialDeliveryDistance != null && Number.isFinite(initialDeliveryDistance) && initialDeliveryDistance >= 0
          ? initialDeliveryDistance
          : (sess.deliveryDistance != null && Number.isFinite(sess.deliveryDistance) && sess.deliveryDistance >= 0 ? sess.deliveryDistance : null)),
  );
  const [deliveryAirportCode, setDeliveryAirportCode] = useState<string | null>(seededDeliveryAirportCode);
  const [deliveryError, setDeliveryError] = useState<string | null>(null);
  const [locationPromptShown, setLocationPromptShown] = useState(false);
  // A location is only "resolved" once it is a configured airport or a
  // geocoded address with a computed distance (0 is a valid distance).
  // Free text / a restored label without distance is never priced.
  const locationResolved = !deliveryError && (!!deliveryAirportCode || (!!deliveryAddress && deliveryDistance != null));
  const [geocoding, setGeocoding] = useState(false);
  const [addressSuggestions, setAddressSuggestions] = useState<Array<{ display_name: string; lat: string; lon: string; airport_code?: string; street?: string; city?: string; state?: string }>>([]);
  const addressSearchSeqRef = useRef(0);
  // Bumped by every explicit renter location action so a slower automatic
  // city prefill can never overwrite it.
  const locationEpochRef = useRef(0);
  const mobileFooterRef = useRef<HTMLDivElement>(null);
  const [keyboardInset, setKeyboardInset] = useState(0);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const suggestionsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const deliveryInputRef = useRef<HTMLInputElement>(null);
  const deliveryErrorRef = useRef<HTMLDivElement>(null);
  const timeRangeErrorRef = useRef<HTMLParagraphElement>(null);
  const timedConflictErrorRef = useRef<HTMLParagraphElement>(null);
  const mobileBookingScrollRef = useRef<HTMLDivElement>(null);
  const [deliveryInputFocused, setDeliveryInputFocused] = useState(false);

  const isInsurable = !selfDriveUnavailable && computeIsInsurable(car, activeTab);
  const coverageContext = coverageConfirmationContext(car.id, activeTab, car.country_code, car.state, car.insured_value);
  const insuranceIdForQuote = isInsurable ? selectedInsurancePackageId : undefined;

  // Task #3972 (F.12) — derive the vehicle's IANA timezone from the location
  // data the car already carries (lat/lng + country_code) so the Event Rental
  // step can label pick-up / drop-off times in the CAR's local time. Display
  // only; the quote/availability math is untouched. No DB column / backfill.
  const carTimezone = useMemo(
    () => deriveCarTimezone(car),
    [car.latitude, car.longitude, car.country_code],
  );

  // Sync dates when the parent (listings page calendar) selects new dates.
  //
  // The guard below solves a session-restore conflict: on the car-detail page,
  // CarDetailPage initialises `bookingDateFrom`/`bookingDateTo` to `""` when
  // there are no URL params, and those empty strings are always forwarded as
  // `dateFrom`/`dateTo` props. Without a guard the effect fires on mount with
  // `""` and immediately overwrites any date restored from sessionStorage.
  //
  // Solution: on the very first effect invocation, skip if the prop is empty
  // so session-restored dates survive.  Every subsequent change — including a
  // deliberate parent clear back to `""` — propagates normally, preserving the
  // full parent-driven-update contract.
  // Sanitize every parent-driven date: a stale / corrupted / localized
  // query-param value (the parent CarDetailPage derives these from the URL)
  // that isn't a well-formed YYYY-MM-DD is treated as unset, so it can never
  // be rehydrated into widget state and forwarded to the quote endpoint (which
  // 400s `Invalid date format`, flooding Sentry).
  const externalFromMountedRef = useRef(false);
  useEffect(() => {
    if (!externalFromMountedRef.current) {
      externalFromMountedRef.current = true;
      if (!externalFrom) return;
    }
    setDateFrom(isValidIsoDate(externalFrom) ? externalFrom : "");
  }, [externalFrom]);

  const externalToMountedRef = useRef(false);
  useEffect(() => {
    if (!externalToMountedRef.current) {
      externalToMountedRef.current = true;
      if (!externalTo) return;
    }
    setDateTo(isValidIsoDate(externalTo) ? externalTo : "");
  }, [externalTo]);

  const selfDriveDeliveryEnabled = car.self_drive_delivery_enabled === true;
  // Self-drive delivery is gated ONLY by the dedicated self-drive flag (the
  // aggregate delivery flag is ignored for self-drive). Chauffeured / event
  // always need a renter-supplied meeting location, so the editor is always
  // available for them and a resolved location is REQUIRED before quoting.
  const deliveryAllowedForTab = activeTab === "driving" ? !!selfDriveDeliveryEnabled : true;
  const locationRequiredMissing = (activeTab === "chauffeured" || activeTab === "event") && !locationResolved;

  // Persist widget inputs to sessionStorage so they survive same-tab
  // navigation. Skipped in preview mode to avoid polluting session storage
  // with demo data. Debounced to avoid a write on every keystroke.
  useEffect(() => {
    if (isPreview) return;
    const timer = setTimeout(() => {
      writeBwSession(car.id, {
        // The hidden desktop widget shares this key. Its ordinary input save
        // must not overwrite a plan committed by the mobile reserve flow.
        insurancePackageId: mobile ? selectedInsurancePackageId : readBwSession(car.id).insurancePackageId,
        dateFrom,
        dateTo,
        pickupTime,
        dropoffTime,
        activeTab,
        deliveryAddress: deliveryAllowedForTab ? deliveryAddress : "",
        deliveryAirportCode: deliveryAllowedForTab ? deliveryAirportCode : null,
        deliveryDistance: deliveryAllowedForTab ? deliveryDistance : null,
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [isPreview, car.id, dateFrom, dateTo, pickupTime, dropoffTime, activeTab, deliveryAddress, deliveryAirportCode, deliveryDistance, deliveryAllowedForTab, selectedInsurancePackageId, mobile]);

  // Mobile reserve drawer: one question per screen (trip -> location when
  // the existing rules allow/require it -> review with the real Reserve CTA).
  // Presentation only: every section stays mounted, so quotes, validation and
  // session persistence are unchanged; intermediate Next never books.
  type MobileStep = "trip" | "location" | "coverage" | "review";
  const compactHourlyMode = mobile && (activeTab === "chauffeured" || activeTab === "event");
  const mobileSteps: MobileStep[] = compactHourlyMode ? ["review"] : [
    "trip",
    ...(deliveryAllowedForTab ? (["location"] as const) : []),
    // Only where the existing insured-plan choice applies (US self-drive
    // within the insurable value band); every other market skips it.
    ...(isInsurable ? (["coverage"] as const) : []),
    "review",
  ];
  const [mobileStepRaw, setMobileStep] = useState<MobileStep>("trip");
  const mobileStep: MobileStep = mobileSteps.includes(mobileStepRaw) ? mobileStepRaw : "review";
  const mobileStepIndex = mobileSteps.indexOf(mobileStep);
  const stepCls = (step: MobileStep) => !mobile ? "contents"
    : compactHourlyMode ? "bw-step-section bw-step-compact"
    : cn("bw-step-section", mobileStep !== step && "hidden");
  const current = tabs.find(t => t.key === activeTab) || tabs[0];
  const price = current?.price;
  const isHourlyType = activeTab === "chauffeured" || activeTab === "event";
  const locationCopy = bookingLocationCopy(activeTab, t);
  const twelveHourClock = usesTwelveHourClock(market, i18n?.language);
  const onDateSelectRef = useRef(onDateSelect);
  onDateSelectRef.current = onDateSelect;
  const hourlyEnd = useMemo(
    () => (activeTab === "chauffeured" && dateFrom && isValidIsoDate(dateFrom) ? deriveHourlyEnd(dateFrom, pickupTime, hourlyHours) : null),
    [activeTab, dateFrom, pickupTime, hourlyHours],
  );
  // Only chauffeur derives its end from duration. Events retain the explicit
  // start/end dates and clocks, including restored intervals beyond 24 hours.
  const hourlyQuoteDateTo = hourlyEnd ? hourlyEnd.dateTo : null;
  useEffect(() => {
    if (!hourlyEnd || !hourlyQuoteDateTo) return;
    if (dropoffTime !== hourlyEnd.dropoffTime) setDropoffTime(hourlyEnd.dropoffTime);
    if (dateTo !== hourlyQuoteDateTo) {
      setDateTo(hourlyQuoteDateTo);
      onDateSelectRef.current?.(dateFrom, hourlyQuoteDateTo);
    }
  }, [hourlyEnd, hourlyQuoteDateTo, dateFrom, dateTo, dropoffTime]);
  const hourlyEndLabel = (() => {
    if (!hourlyEnd) return null;
    const parsed = parseBookingTime(hourlyEnd.dropoffTime);
    if (!parsed) return null;
    const clock = formatClock(parsed.hour24, parsed.minute, twelveHourClock);
    return hourlyEnd.dateTo !== dateFrom ? t("booking.end_next_day", "{{time}} (next day)", { time: clock }) : clock;
  })();
  // Compact mobile chauffeur/event form: every quote input, the price and
  // Book in one view. Bypasses the legacy progressive steps for these modes.
  const compactHourly = mobile && isHourlyType;

  // iOS leaves fixed bottom sheets at their layout height while shrinking the
  // visual viewport for the keyboard. Keep the focused address field inside
  // the visible region instead of allowing it to sit behind the keyboard's
  // input accessory. Do not call scrollIntoView here: on iOS it can pan the
  // fixed sheet/document itself on the first focus, leaving the Reserve
  // footer at the top and a large blank region below it. Move only the
  // drawer's nested scroll region instead.
  useEffect(() => {
    if (!mobile || !deliveryInputFocused) return;

    let frame: number | null = null;
    const revealInput = () => {
      if (frame != null) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const input = deliveryInputRef.current;
        const scrollRegion = mobileBookingScrollRef.current;
        if (input && scrollRegion) {
          const visualViewport = window.visualViewport;
          const viewportTop = visualViewport?.offsetTop ?? 0;
          const viewportBottom = viewportTop + (visualViewport?.height ?? window.innerHeight);
          // Leave room for the iOS input accessory / prediction bar as well as
          // the keyboard itself, even where Safari reports it inside the
          // visual viewport.
          const stepHeader = scrollRegion.querySelector<HTMLElement>(".bw-step-header")?.getBoundingClientRect();
          const safeTop = Math.max(viewportTop + 20, stepHeader && stepHeader.height > 0 ? stepHeader.bottom + 12 : 0);
          const rect = input.getBoundingClientRect();
           // The drawer autocomplete stays below the field on mobile. Reserve
           // room for its first few rows so the list does not open underneath
           // the keyboard after iOS scrolls the focused input into view.
           // Inline mode shows at most three rows plus a More results control.
           const desiredSuggestionReserve = showSuggestions
             ? Math.min(3, Math.max(addressSuggestions.length, 1)) * 60 + (addressSuggestions.length > 3 ? 48 : 0)
             : 0;
           // Clear the actual Reserve footer (measured) and whatever part of
           // the layout viewport the keyboard currently covers.
           // Falls back to the previous 88px allowance when unmeasurable.
           const footerRect = mobileFooterRef.current?.getBoundingClientRect();
           const footerTop = footerRect && footerRect.height > 0 ? footerRect.top + 12 : viewportBottom - 88 + 12;
           const scrollRect = scrollRegion.getBoundingClientRect();
           // The host now follows the visible viewport. Only add compensating
           // scroll room if this particular scroll owner still crosses it.
           const inset = Math.max(0, Math.round(
             scrollRect.height > 0 ? scrollRect.bottom - viewportBottom : window.innerHeight - viewportBottom,
           ));
           setKeyboardInset((prev) => (prev === inset ? prev : inset));
           const suggestionReserve = Math.min(desiredSuggestionReserve, Math.max(
             0, Math.min(viewportBottom, footerTop) - 12 - safeTop - rect.height - 16,
           ));
           const safeBottom = Math.min(viewportBottom, footerTop) - 12 - suggestionReserve;
          const delta =
            rect.bottom > safeBottom
              ? rect.bottom - safeBottom
              : rect.top < safeTop
                ? rect.top - safeTop
                : 0;
          if (delta !== 0) {
            scrollRegion.scrollTop = Math.max(0, scrollRegion.scrollTop + delta);
          }
        }
        frame = null;
      });
    };

    revealInput();
    const keyboardSettledTimers = [150, 350, 700].map((delay) =>
      window.setTimeout(revealInput, delay),
    );
    const visualViewport = window.visualViewport;
    visualViewport?.addEventListener("resize", revealInput);
    visualViewport?.addEventListener("scroll", revealInput);
    window.addEventListener("resize", revealInput);

    return () => {
      keyboardSettledTimers.forEach((timer) => window.clearTimeout(timer));
      if (frame != null) cancelAnimationFrame(frame);
      visualViewport?.removeEventListener("resize", revealInput);
      visualViewport?.removeEventListener("scroll", revealInput);
      window.removeEventListener("resize", revealInput);
      setKeyboardInset(0);
    };
  }, [addressSuggestions.length, deliveryInputFocused, mobile, showSuggestions]);

  // Date-logic guard: chauffeur end must match its explicit duration (<=24h).
  // For self-drive and event, times are absolute moments paired with dates, so a
  // drop-off at an earlier clock time is only invalid on a single calendar
  // day (a genuine multi-day overnight return at an earlier hour is fine).
  // Mirrors the guard already in CheckoutDatesEditor; without it
  // `quantizeHours` / the server silently floor a reversed range to a
  // 1-unit minimum and quote a misleading price.
  const timeIssue = useMemo(() => {
    return checkoutTimeIssue({
      rentalType: activeTab,
      dateFrom,
      dateTo: hourlyEnd?.dateTo ?? dateTo,
      pickupTime,
      dropoffTime: hourlyEnd?.dropoffTime ?? dropoffTime,
      timezone: carTimezone,
      durationHours: activeTab === "chauffeured" ? hourlyHours : undefined,
    });
  }, [dateFrom, dateTo, pickupTime, dropoffTime, activeTab, carTimezone, availability?.window?.advance_notice_days, isHourlyType, hourlyHours, hourlyEnd]);
  const timeRangeInvalid = timeIssue !== null;
  useEffect(() => { setServerTimeError(null); }, [dateFrom, dateTo, pickupTime, dropoffTime, activeTab]);
  // Timed availability is intentionally only applied to hourly services.
  // Self-drive keeps the legacy date-only policy, so an interval alone never
  // makes a calendar date unavailable.
  const timedBookingConflict = useMemo(
    () => isHourlyType && hasTimedBookingConflict(
      dateFrom,
      hourlyEnd?.dateTo ?? dateTo,
      pickupTime,
      dropoffTime,
      availability?.timedIntervals,
    ),
    [isHourlyType, dateFrom, dateTo, hourlyEnd?.dateTo, pickupTime, dropoffTime, availability?.timedIntervals],
  );
  const commitTimeSelection = useCallback(() => {
    if (!timeRangeInvalid) return;
    emitFunnel("checkout_time_validation", {
      checkout_journey_id: checkoutFlowIdRef.current, car_id: car.id,
      rental_type: activeTab, state: "editing_invalid",
      elapsed_ms: invalidTimeStartedAtRef.current == null ? undefined : Date.now() - invalidTimeStartedAtRef.current,
    });
  }, [timeRangeInvalid, car.id, activeTab, emitFunnel]);
  useEffect(() => {
    if (timeRangeInvalid && invalidTimeStartedAtRef.current == null) {
      invalidTimeStartedAtRef.current = Date.now();
      emitFunnel("checkout_time_validation", {
        checkout_journey_id: checkoutFlowIdRef.current, car_id: car.id,
        rental_type: activeTab, state: "transient_invalid",
      });
    } else if (!timeRangeInvalid && invalidTimeStartedAtRef.current != null) {
      emitFunnel("checkout_time_validation", {
        checkout_journey_id: checkoutFlowIdRef.current, car_id: car.id,
        rental_type: activeTab, state: "recovered",
        elapsed_ms: Date.now() - invalidTimeStartedAtRef.current,
      });
      invalidTimeStartedAtRef.current = null;
    }
  }, [timeRangeInvalid, activeTab, car.id, emitFunnel]);

  // Identity of the inputs a quote was priced for. Book only proceeds when
  // the visible quote matches the current inputs (never a prior location).
  const currentQuoteKey = [activeTab, dateFrom, dateTo, pickupTime, dropoffTime, isHourlyType ? hourlyHours : "", insuranceIdForQuote ?? "", deliveryAddress, deliveryDistance ?? "", deliveryAirportCode ?? "", deliveryLatitude ?? "", deliveryLongitude ?? ""].join("|");
  const [quoteKey, setQuoteKey] = useState<string | null>(null);
  const quoteFresh = !!quote && quoteKey === currentQuoteKey;
  const lastRequestedQuoteKeyRef = useRef<string | null>(null);
  const fetchQuote = useCallback(() => {
    if (!dateFrom || !dateTo || !car.id) return;
    // A New York registration-state listing cannot be booked for self-drive.
    // Do not keep a stale price visible or ask the quote API for renter
    // coverage on a rental type the UI has already disabled.
    if (selfDriveUnavailable && activeTab === "driving") {
      if (quoteAbortRef.current) quoteAbortRef.current.abort();
      setQuote(null);
      setQuoteError(null);
      setLoadingQuote(false);
      return;
    }
    // Chauffeured / event: no quote (and no stale total) until a valid
    // airport or geocoded address is selected in the widget.
    if (locationRequiredMissing) {
      if (quoteAbortRef.current) quoteAbortRef.current.abort();
      setQuote(null);
      setQuoteError(null);
      setLoadingQuote(false);
      return;
    }
    // Never send a malformed date to the quote endpoint — the server 400s with
    // `Invalid date format. Use YYYY-MM-DD` and that expected, data-level
    // rejection was flooding Sentry. A non-ISO value here means a stale /
    // corrupted restored date slipped through; drop any stale quote and treat
    // it like the "pick your dates" empty state instead of quoting.
    if (!isValidIsoDate(dateFrom) || !isValidIsoDate(dateTo)) {
      if (quoteAbortRef.current) quoteAbortRef.current.abort();
      setQuote(null);
      setQuoteError(null);
      setLoadingQuote(false);
      // Task #7234 — funnel: a selected range that can never quote.
      void import("@/lib/analytics").then(({ track }) =>
        track("date_range_rejected", buildDateRangeRejectionProps({
          checkoutJourneyId: checkoutFlowIdRef.current,
          carId: car.id,
          rentalType: activeTab,
          dateFrom,
          dateTo,
          pickupTime,
          dropoffTime,
          drivingMinimumDays: (car as CarData & { driving_rental_min_duration?: number | null }).driving_rental_min_duration,
        }))).catch(() => {});
      return;
    }
    // Reversed / zero-length time range — never quote a misleading price.
    // The inline error under the time pickers explains why; drop any stale
    // quote so the total can't show a bogus 1-unit minimum. (The funnel
    // event for this rejection is emitted by the debounce effect's early-out
    // — this branch is a belt-and-braces guard that normally never runs.)
    if (timeRangeInvalid) {
      if (quoteAbortRef.current) quoteAbortRef.current.abort();
      setQuote(null);
      setQuoteError(null);
      setLoadingQuote(false);
      return;
    }
    if (quoteAbortRef.current) quoteAbortRef.current.abort();
    const controller = new AbortController();
    quoteAbortRef.current = controller;

    // Task #2751 — event rentals MUST NOT send an `hours` value: the
    // server computes elapsed hours from pickupTime / dropoffTime and
    // applies the true 24-hour rule. Chauffeur sends its explicit duration.
    const hoursToSend = activeTab === "chauffeured"
      ? hourlyHours
      : undefined;
    const requestKey = currentQuoteKey;
    lastRequestedQuoteKeyRef.current = requestKey;

    const distanceToSend = deliveryAllowedForTab && locationResolved && deliveryDistance != null && Number.isFinite(deliveryDistance) && deliveryDistance >= 0 && !deliveryAirportCode ? deliveryDistance : undefined;
    const airportToSend = deliveryAllowedForTab && locationResolved ? deliveryAirportCode || undefined : undefined;

    setLoadingQuote(true);
    setQuoteError(null);
    setCutoffRejected(false);
    // Every mode needs its real clocks. Chauffeur's overnight guard verifies
    // the explicit duration against these clocks and the derived end date.
    const pickupTimeForQuote = pickupTime;
    const dropoffTimeForQuote = dropoffTime;
    bookingsApi.getQuote(
      car.id, dateFrom, dateTo, activeTab, insuranceIdForQuote ?? undefined, undefined, undefined,
      hoursToSend, distanceToSend, airportToSend, pickupTimeForQuote, dropoffTimeForQuote, undefined,
      undefined, undefined, undefined, undefined, undefined,
      !deliveryAllowedForTab || !locationResolved || deliveryAirportCode ? null : deliveryAddress || null,
      !deliveryAllowedForTab || !locationResolved || deliveryAirportCode ? null : deliveryLatitude,
      !deliveryAllowedForTab || !locationResolved || deliveryAirportCode ? null : deliveryLongitude,
    )
      .then(q => {
        if (!controller.signal.aborted) {
          setQuote(q);
          setQuoteKey(requestKey);
          setQuoteError(null);
          // Task #7234 — funnel: a priced quote reached the renter.
          void import("@/lib/analytics").then(({ track }) =>
            track("price_quote_shown", {
              car_id: car.id,
              total_price: q.totalIncludingTax ?? q.paymentDue ?? null,
              currency: q.currency ?? null,
              num_days: q.numDays ?? null,
              rental_type: activeTab,
              service_mode: serviceModeFor(activeTab),
              mode: serviceModeFor(activeTab),
            })).catch(() => {});
        }
      })
      .catch((err) => {
        if (!controller.signal.aborted) {
          if (!isUsPickupDeadlineError(err)) console.error("[BookingWidget] Quote fetch failed:", err);
          setQuote(null);
          if (isUsPickupDeadlineError(err)) {
            setCutoffRejected(true);
            setQuoteError(usPickupDeadlineMessage(err, i18n.language));
            setServerTimeError({ reason: "pickup_deadline", message: usPickupDeadlineMessage(err, i18n.language) });
            return;
          }
          if (err instanceof QuoteApiError && err.status === 400 &&
              /time|date|notice|duration|pickup|minimum|cutoff/i.test(`${err.code || ""} ${err.message}`)) {
            setServerTimeError({ reason: err.code || "server_time_validation", message: err.message });
          }
          // Task #7234 — funnel: why no price was shown. Reason is the
          // server's machine code when present (dates_blocked,
          // below_min_days, no_price, delivery_beyond_max_radius…),
          // else a coarse client classification. No PII.
          {
            const reason = err instanceof QuoteApiError && err.code
              ? String(err.code).toLowerCase()
              : (err instanceof QuoteApiError && err.status === 400)
                ? "validation_400"
                : (err instanceof TypeError || !err?.message || err?.message === "Failed to fetch")
                  ? "network_error"
                  : "server_error";
            void import("@/lib/analytics").then(({ track }) =>
              track("price_quote_unavailable", { car_id: car.id, reason, rental_type: activeTab, service_mode: serviceModeFor(activeTab), mode: serviceModeFor(activeTab) })).catch(() => {});
          }
          if (err instanceof QuoteApiError && err.code === "DELIVERY_BEYOND_MAX_RADIUS") {
            const max = err.maxRadius ?? car.delivery_radius_full ?? car.delivery_maximum_distance ?? 0;
            trackBookingBox("location_error", car.id, activeTab, { reason: "outside_service_area" });
            setDeliveryError(
              t("booking.delivery_too_far_radius", "This location is outside the car's delivery range (maximum {{max}} {{unit}}).", {
                max,
                unit: getDistanceUnitPlural(car.country_code),
              })
            );
            setQuoteError(null);
            return;
          }
          // An expected client-validation rejection (HTTP 400 — e.g.
          // `Invalid date format`, missing required fields) is a handled,
          // user/data-level error: surface it inline but DON'T capture it as a
          // Sentry exception, which was flooding error monitoring. Genuinely
          // unexpected failures (5xx, network) are still reported.
          const isExpectedValidation = err instanceof QuoteApiError && (
            err.status === 400 ||
            (err.status === 422 && err.code === "SERVICE_LOCATION_REQUIRED")
          );
          if (!isExpectedValidation) Sentry.captureException(err);
          const isNetworkError = err instanceof TypeError || !err?.message || err?.message === "Failed to fetch";
          setQuoteError(
            isNetworkError
              ? t("booking.quote_error", "Unable to calculate price. Please try different dates.")
              : err.message
          );
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingQuote(false);
      });
  }, [currentQuoteKey, hourlyHours, dateFrom, dateTo, activeTab, pickupTime, dropoffTime, car.id, isHourlyType, insuranceIdForQuote, deliveryAddress, deliveryLatitude, deliveryLongitude, deliveryDistance, deliveryAirportCode, deliveryAllowedForTab, timeRangeInvalid, selfDriveUnavailable, locationRequiredMissing, locationResolved]);

  useEffect(() => {
    // Location cleared / changed to unresolved / tab switched to a
    // location-required type: drop the stale total and abort in-flight now.
    if (locationRequiredMissing) {
      if (quoteAbortRef.current) quoteAbortRef.current.abort();
      setQuote(null);
      setQuoteError(null);
      setLoadingQuote(false);
      return undefined;
    }
    if (dateFrom && dateTo) {
      // Reversed / zero-length range — clear any stale quote immediately
      // (skip the 300ms debounce) so a bogus total can't linger beside the
      // inline "drop-off must be after pick-up" error.
      if (timeRangeInvalid) {
        if (quoteAbortRef.current) quoteAbortRef.current.abort();
        setQuote(null);
        setQuoteError(null);
        setLoadingQuote(false);
        // Editing an incomplete interval is not a rejected booking attempt.
        // The time-validation effect records editing/recovery separately.
        return undefined;
      }
      // Task #3945 — keep the previous quote mounted during a recalc so the
      // total can churn in place (the RollingNumber animates while
      // `loadingQuote` is true). Nulling the quote here would unmount the
      // whole total block and fall back to the "Calculating…" spinner,
      // defeating the odometer animation. The quote is only cleared below
      // when the dates are removed (nothing left to price).
      const timer = setTimeout(fetchQuote, 300);
      return () => clearTimeout(timer);
    }
    setQuote(null);
    return undefined;
  }, [dateFrom, dateTo, activeTab, pickupTime, dropoffTime, fetchQuote, insuranceIdForQuote, deliveryDistance, deliveryAirportCode, timeRangeInvalid, locationRequiredMissing]);

  const buildAddressSearchUrl = useCallback((query: string) => {
    // Bias autocomplete results toward the car's location: pickup/drop-off
    // is virtually always near the car, so passing the car's lat/lng and
    // country to the API lets it rank in-country, nearby matches above
    // far-away strings that share the same street name. We only include
    // the bias params when the car actually has coordinates — without a
    // precise anchor the API's existing IP-based bias is more accurate
    // than a country-only hint, and the task explicitly requires the
    // no-coordinate path to fall back to today's IP behaviour.
    const sp = new URLSearchParams({ q: query });
    // Mobile drawer only: opt into venue-preserving booking results. Desktop
    // requests stay byte-identical.
    if (mobile) sp.set("context", "booking");
    if (car.latitude != null && car.longitude != null) {
      sp.set("lat", String(car.latitude));
      sp.set("lng", String(car.longitude));
      if (car.country_code) sp.set("country", car.country_code);
    }
    return `${BW_API_BASE}/address/search?${sp.toString()}`;
  }, [car.latitude, car.longitude, car.country_code, mobile]);

  const fetchAddressSuggestions = useCallback((query: string) => {
    if (suggestionsTimerRef.current) clearTimeout(suggestionsTimerRef.current);
    const seq = ++addressSearchSeqRef.current;
    if (query.trim().length < 3) {
      setAddressSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    suggestionsTimerRef.current = setTimeout(async () => {
      try {
        const res = await fetch(buildAddressSearchUrl(query));
        const data = await res.json();
        // Drop responses superseded by a newer query, a selection or a clear.
        if (seq !== addressSearchSeqRef.current) return;
        const results = data.results;
        if (Array.isArray(results) && results.length > 0) {
          setAddressSuggestions(results
            .filter((r: Record<string, unknown>) => typeof r.displayName === "string" && typeof r.latitude === "number" && typeof r.longitude === "number")
            .map((r: Record<string, unknown>) => ({ display_name: r.displayName as string, lat: String(r.latitude), lon: String(r.longitude), airport_code: typeof r.airportCode === "string" ? r.airportCode : undefined, street: optionalString(r.street), city: optionalString(r.city), state: optionalString(r.state) })));
          setShowSuggestions(true);
        } else {
          setAddressSuggestions([]);
          setShowSuggestions(false);
        }
      } catch {
        if (seq !== addressSearchSeqRef.current) return;
        setAddressSuggestions([]);
        setShowSuggestions(false);
      }
    }, 400);
  }, [buildAddressSearchUrl]);

  const handleDeliverySelect = useCallback(async (
    displayName: string, lat: number, lon: number, airportCodeHint?: string,
    opts: { source?: "autocomplete" | "enter" | "book_click"; guard?: () => boolean; skipAirportMatch?: boolean } = {},
  ): Promise<boolean> => {
    // Invalidate any in-flight suggestion lookup so it cannot reopen the list.
    addressSearchSeqRef.current += 1;
    if (suggestionsTimerRef.current) clearTimeout(suggestionsTimerRef.current);
    if (car.latitude == null || car.longitude == null) {
      setDeliveryError(t("booking.delivery_not_available", "Delivery location could not be determined"));
      trackBookingBox("location_error", car.id, activeTab, { reason: "car_location_unknown" });
      return false;
    }

    setDeliveryError(null);
    setShowSuggestions(false);
    setAddressSuggestions([]);

    // Task #4794 — match the host's configured flat airport fee by exact
    // IATA code first (carried on canonical airport suggestions), falling
    // back to fuzzy name/token matching only for codeless geocoder
    // results. Shared with CheckoutLocationEditor so the two copies can't
    // drift apart and silently fall through to per-mile distance pricing.
    const matchedAirport = opts.skipAirportMatch ? null : matchDeliveryAirport(deliveryAirports, displayName, airportCodeHint);

    if (matchedAirport) {
      setDeliveryAirportCode(matchedAirport.airport_code);
      setDeliveryDistance(null);
      setDeliveryLatitude(null);
      setDeliveryLongitude(null);
      setDeliveryAddress(`${matchedAirport.airport_name} (${matchedAirport.airport_code})`);
      setDeliveryEditing(false);
      setDeliveryInputValue("");
      if (opts.source) trackBookingBox("location_changed", car.id, activeTab, { source: opts.source });
      return true;
    }

    setGeocoding(true);
    let roundedDistance: number;
    try {
      const sp = new URLSearchParams({
        originLat: String(car.latitude),
        originLon: String(car.longitude),
        destLat: String(lat),
        destLon: String(lon),
        countryCode: car.country_code || "",
      });
      const res = await fetch(`${BW_API_BASE}/delivery/distance?${sp.toString()}`);
      if (res.ok) {
        const data = await res.json();
        roundedDistance = typeof data.distance === "number" && data.distance >= 0 ? data.distance : Math.round(haversineDistance(car.latitude, car.longitude, lat, lon, isMetricCountry(car.country_code)) * 10) / 10;
      } else {
        roundedDistance = Math.round(haversineDistance(car.latitude, car.longitude, lat, lon, isMetricCountry(car.country_code)) * 10) / 10;
      }
    } catch {
      roundedDistance = Math.round(haversineDistance(car.latitude, car.longitude, lat, lon, isMetricCountry(car.country_code)) * 10) / 10;
    }
    setGeocoding(false);
    // A newer explicit renter choice superseded this (e.g. automatic prefill).
    if (opts.guard && !opts.guard()) return false;

    const maxRadius = car.delivery_radius_full ?? car.delivery_maximum_distance;
    if (maxRadius != null && roundedDistance > maxRadius) {
      setDeliveryDistance(null);
      setDeliveryAirportCode(null);
      setDeliveryLatitude(null);
      setDeliveryLongitude(null);
      setDeliveryAddress("");
      setDeliveryError(
        t("booking.delivery_too_far", "This location is outside the car's delivery range ({{distance}} {{unit}} exceeds the maximum of {{max}} {{unit}}).", {
          distance: roundedDistance,
          max: maxRadius,
          unit: getDistanceUnitPlural(car.country_code),
        })
        + " " + t("booking.outside_service_area", "Outside service area."),
      );
      trackBookingBox("location_error", car.id, activeTab, { reason: "outside_service_area" });
      return false;
    }

    setDeliveryDistance(roundedDistance);
    setDeliveryAirportCode(null);
    setDeliveryLatitude(lat);
    setDeliveryLongitude(lon);
    setDeliveryAddress(displayName);
    setDeliveryEditing(false);
    setDeliveryInputValue("");
    if (opts.source) trackBookingBox("location_changed", car.id, activeTab, { source: opts.source });
    return true;
  }, [car.id, activeTab, car.latitude, car.longitude, car.delivery_radius_full, car.delivery_maximum_distance, car.country_code, deliveryAirports, t]);

  const handleDeliveryGeocode = useCallback(async (address: string, source: "enter" | "book_click" = "enter"): Promise<boolean> => {
    if (!address.trim()) return false;
    locationEpochRef.current += 1;
    if (car.latitude == null || car.longitude == null) {
      setDeliveryError(t("booking.delivery_not_available", "Delivery location could not be determined"));
      trackBookingBox("location_error", car.id, activeTab, { reason: "car_location_unknown" });
      return false;
    }
    setGeocoding(true);
    setDeliveryError(null);
    setShowSuggestions(false);

    try {
      const res = await fetch(buildAddressSearchUrl(address));
      const data = await res.json();
      const results = data.results;
      if (!Array.isArray(results) || results.length === 0) {
        setDeliveryError(t("booking.address_not_found", "Address not found. Please try a more specific address."));
        trackBookingBox("location_error", car.id, activeTab, { reason: "not_found" });
        setGeocoding(false);
        return false;
      }

      const first = results[0];
      return await handleDeliverySelect(
        first.displayName || address,
        first.latitude,
        first.longitude,
        typeof first.airportCode === "string" ? first.airportCode : undefined,
        { source },
      );
    } catch (err) {
      console.error("[BookingWidget] Geocoding failed:", err);
      setDeliveryError(t("booking.geocoding_failed", "Could not look up that address. Please try again."));
      trackBookingBox("location_error", car.id, activeTab, { reason: "geocoder_unavailable" });
      return false;
    } finally {
      setGeocoding(false);
    }
  }, [car.id, activeTab, car.latitude, car.longitude, handleDeliverySelect, buildAddressSearchUrl, t]);

  const clearDeliveryLocation = useCallback(() => {
    addressSearchSeqRef.current += 1;
    locationEpochRef.current += 1;
    if (suggestionsTimerRef.current) clearTimeout(suggestionsTimerRef.current);
    setDeliveryAddress("");
    setDeliveryInputValue("");
    setDeliveryDistance(null);
    setDeliveryAirportCode(null);
    setDeliveryLatitude(null);
    setDeliveryLongitude(null);
    setDeliveryError(null);
    setDeliveryEditing(false);
    setAddressSuggestions([]);
    setShowSuggestions(false);
  }, []);

  // A draft/session/return link must not restore a delivery location on
  // self-drive when this listing has opted out of that rental mode.
  useEffect(() => {
    if (activeTab === "driving" && !selfDriveDeliveryEnabled) clearDeliveryLocation();
  }, [activeTab, selfDriveDeliveryEnabled, clearDeliveryLocation]);
  // A stale text-only delivery draft is not a priced delivery selection.
  // Fall back to the car's pickup location; delivery is selected in checkout.
  useEffect(() => {
    if (deliveryAddress && deliveryDistance == null && !deliveryAirportCode && !deliveryEditing) clearDeliveryLocation();
  }, [deliveryAddress, deliveryDistance, deliveryAirportCode, deliveryEditing, clearDeliveryLocation]);

  // Prefill the meeting point with the car's own city centre, resolved by the
  // real geocoder + distance service (no invented coordinates or distance),
  // so chauffeur / event renters see a full price immediately. Any explicit
  // URL/session location, or any renter action before it resolves, wins.
  const [prefillState, setPrefillState] = useState<"idle" | "pending" | "done" | "error">("idle");
  const [prefilledAddress, setPrefilledAddress] = useState<string | null>(null);
  const prefillAttemptedRef = useRef(!!seededDeliveryAddress || !!seededDeliveryAirportCode);
  const prefillCity = car.city?.trim() || "";
  const runCityPrefill = useCallback(async () => {
    if (!prefillCity || car.latitude == null || car.longitude == null) {
      setPrefillState("error");
      trackBookingBox("location_error", car.id, activeTab, { reason: "prefill_no_city" });
      return;
    }
    const epoch = ++locationEpochRef.current;
    const stillCurrent = () => locationEpochRef.current === epoch;
    setPrefillState("pending");
    try {
      const res = await fetch(buildAddressSearchUrl([prefillCity, car.state].filter(Boolean).join(", ")));
      if (!res.ok) throw new Error(`geocoder_${res.status}`);
      const data = await res.json();
      if (!stillCurrent()) return;
      const first = Array.isArray(data?.results) ? data.results[0] : null;
      if (!first || typeof first.latitude !== "number" || typeof first.longitude !== "number") throw new Error("no_result");
      const label = typeof first.displayName === "string" && first.displayName ? first.displayName : prefillCity;
      const ok = await handleDeliverySelect(label, first.latitude, first.longitude, undefined, { guard: stillCurrent, skipAirportMatch: true });
      if (!stillCurrent()) return;
      if (!ok) { setPrefillState("error"); return; }
      setPrefilledAddress(label);
      setPrefillState("done");
      trackBookingBox("location_prefilled", car.id, activeTab, { city: prefillCity });
    } catch {
      if (!stillCurrent()) return;
      setPrefillState("error");
      trackBookingBox("location_error", car.id, activeTab, { reason: "prefill_unavailable" });
    }
  }, [prefillCity, car.id, car.state, car.latitude, car.longitude, activeTab, buildAddressSearchUrl, handleDeliverySelect]);
  useEffect(() => {
    if (isPreview || !isHourlyType || prefillAttemptedRef.current || locationResolved || deliveryEditing) return;
    prefillAttemptedRef.current = true;
    void runCityPrefill();
  }, [isPreview, isHourlyType, locationResolved, deliveryEditing, runCityPrefill]);
  const showingPrefill = isHourlyType && prefillState === "done" && !!prefilledAddress && deliveryAddress === prefilledAddress && locationResolved;

  const buildCheckoutUrl = (messageToOwner?: string) => {
    // Task #2751 — event rentals don't forward an `hours` URL param;
    // CheckoutPage re-quotes from pickupTime/dropoffTime and the server
    // applies true 24-hour billing. Chauffeur still forwards hours so
    // CheckoutPage can render the same total the widget previewed.
    const hoursToSend = activeTab === "chauffeured"
      ? hourlyHours
      : undefined;
    // Task #2420 / #3060 — forward the human-readable delivery address
    // through to /checkout. The widget already captured it in
    // `deliveryAddress` when the renter typed/picked their location; if
    // we don't put it on the URL the renter lands on CheckoutPage with
    // no address in state, my POST /bookings handler has nothing to
    // send, and trips.delivery_address ends up NULL — the exact bug.
    // Always forward the typed address when present (and no airport is
    // selected), independent of distance: chauffeured/event rentals
    // commonly resolve to distance 0 when the renter's location is at
    // the car's pickup point, and dropping the address in that case
    // forces re-entry after the login redirect (#3060). Distance is
    // still gated on > 0 so a 0-distance pickup doesn't add a noisy
    // (and surcharge-implying) `delivery_distance=0` param.
    const deliveryResolved = !deliveryError && (!!deliveryAirportCode || deliveryDistance != null);
    const addressForUrl = deliveryAllowedForTab && deliveryResolved && !deliveryAirportCode && deliveryAddress ? deliveryAddress : "";
    // Forward the renter's previously-typed message back through to
    // checkout so a Back-then-Reserve round-trip preserves it (#1699).
    // Caller-supplied value (e.g. an in-widget message field) wins,
    // otherwise we fall back to the URL-supplied initial value.
    const messageForUrl = messageToOwner ?? initialMessage ?? "";
    writeSecureCheckoutState(checkoutFlowIdRef.current, {
      dateFrom,
      dateTo,
      rentalType: activeTab,
      pickupTime,
      dropoffTime,
      hours: hoursToSend,
      insurancePackageId: insuranceIdForQuote ?? undefined,
      coverageConfirmation: isInsurable && widgetConfirmation?.context === coverageContext
        && widgetConfirmation.packageId === selectedInsurancePackageId ? widgetConfirmation : undefined,
      numPassengers: activeTab === "chauffeured" ? numPassengers : undefined,
      deliveryAddress: addressForUrl.slice(0, 500) || undefined,
      deliveryDistance: deliveryAllowedForTab && deliveryResolved && !deliveryAirportCode && deliveryDistance != null && deliveryDistance >= 0 ? deliveryDistance : undefined,
      airportCode: deliveryAllowedForTab && deliveryResolved ? deliveryAirportCode || undefined : undefined,
      message: messageForUrl || undefined,
      occasion: occasion || undefined,
    }, user?.id, canClaimCheckoutFlowAfterAuth(checkoutFlowIdRef.current));
    retainListingFlow();
    return localizedHref(`/checkout?car=${car.id}&h=${encodeURIComponent(checkoutFlowIdRef.current)}`);
  };

  // Task #7234 — funnel: the checkout CTA became visible for this car
  // (once per car per widget mount; the CTA is unconditionally rendered
  // inside the widget, so mount ≈ shown).
  const ctaShownForCar = useRef<number | null>(null);
  useEffect(() => {
    if (ctaShownForCar.current === car.id) return;
    ctaShownForCar.current = car.id;
    void import("@/lib/analytics").then(({ track }) =>
      track("checkout_cta_shown", { car_id: car.id })).catch(() => {});
  }, [car.id]);

  const reserveEntryInFlightRef = useRef(false);
  const handleBook = async () => {
    if (authLoading || reserveEntryInFlightRef.current || authModal.isOpen) return;
    reserveEntryInFlightRef.current = true;
    try {
    // Task #7234 — funnel: CTA click, before any validation branch so
    // every attempt is counted (the branches below explain drop-offs).
    const attemptId = createCheckoutFlowId();
    const outcome = (value: "opened_checkout" | "missing_dates" | "missing_quote" | "missing_delivery_location" | "delivery_location_invalid" | "delivery_recalculation" | "insurance_choice" | "authentication_required" | "invalid_time_range", rejectionReason?: string) =>
      emitFunnel("checkout_cta_outcome", {
        checkout_journey_id: checkoutFlowIdRef.current, attempt_id: attemptId,
        car_id: car.id, rental_type: activeTab, outcome: value,
        ...checkoutSelectionProperties({ rentalType: activeTab, dateFrom, dateTo, pickupTime, dropoffTime, timezone: carTimezone }),
        rejection_reason: rejectionReason || (value === "invalid_time_range"
          ? timeIssue?.reason || serverTimeError?.reason || "invalid_time_range"
          : value === "opened_checkout" ? null : value),
      });
    void import("@/lib/analytics").then(({ track }) => {
      track("checkout_cta_clicked", { checkout_journey_id: checkoutFlowIdRef.current, attempt_id: attemptId, car_id: car.id, rental_type: activeTab, has_quote: !!quote });
    }).catch(() => { /* Analytics must never delay or prevent checkout entry. */ });
    if (cutoffRejected || serverTimeError) {
      outcome("invalid_time_range", serverTimeError?.reason || "pickup_deadline");
      requestAnimationFrame(() => timeRangeErrorRef.current?.focus());
      return;
    }
    if (timedBookingConflict) {
      outcome("invalid_time_range", "booking_overlap_or_turnaround_buffer");
      setQuoteError(t("booking.timed_booking_conflict", "Another booking overlaps the selected time or the required one-hour preparation/turnaround buffer. Please choose a different time."));
      requestAnimationFrame(() => timedConflictErrorRef.current?.focus());
      return;
    }
    if (selfDriveUnavailable && activeTab === "driving") {
      setQuoteError(isNewYorkListing
        ? t("booking.ny_self_drive_unavailable", "Self-drive is unavailable for vehicles registered in New York. Choose another rental type.")
        : t("booking.unverified_self_drive_unavailable", "Self-drive availability cannot be confirmed because this vehicle's registration state is unknown."));
      return;
    }
    if (timeRangeInvalid) {
      outcome("invalid_time_range");
      emitFunnel("checkout_time_validation", {
        checkout_journey_id: checkoutFlowIdRef.current,
        car_id: car.id,
        rental_type: activeTab,
        state: "blocked_submission",
      });
      void import("@/lib/analytics").then(({ track }) =>
        track("date_range_rejected", buildDateRangeRejectionProps({
          checkoutJourneyId: checkoutFlowIdRef.current,
          carId: car.id,
          rentalType: activeTab,
          dateFrom,
          dateTo,
          pickupTime,
          dropoffTime,
          drivingMinimumDays: (car as CarData & { driving_rental_min_duration?: number | null }).driving_rental_min_duration,
        }))).catch(() => {});
      requestAnimationFrame(() => timeRangeErrorRef.current?.focus());
      return;
    }
    // Chauffeur / event: a typed-but-unresolved address is accepted on Book
    // (no separate Go step). Book then waits for the quote recalculated for
    // that address rather than continuing with the prior location's price.
    if (isHourlyType && deliveryEditing && deliveryInputValue.trim()) {
      const accepted = await handleDeliveryGeocode(deliveryInputValue, "book_click");
      if (!accepted) {
        outcome("delivery_location_invalid");
        requestAnimationFrame(() => deliveryErrorRef.current?.focus());
        return;
      }
      outcome("delivery_recalculation");
      pendingBookRef.current = true;
      setPendingBookTick(x => x + 1);
      return;
    }
    if (isHourlyType && prefillState === "pending" && !locationResolved) {
      outcome("delivery_recalculation");
      pendingBookRef.current = true;
      setPendingBookTick(x => x + 1);
      return;
    }
    if (isHourlyType && !locationRequiredMissing && dateFrom && dateTo && !quoteFresh
      && (loadingQuote || lastRequestedQuoteKeyRef.current !== currentQuoteKey || !!quote)) {
      outcome("delivery_recalculation");
      pendingBookRef.current = true;
      setPendingBookTick(x => x + 1);
      return;
    }
    if (locationRequiredMissing) {
      outcome("missing_delivery_location");
      setLocationPromptShown(true);
      setDeliveryEditing(true);
      requestAnimationFrame(() => deliveryInputRef.current?.focus());
      return;
    }
    if (!dateFrom || !dateTo) {
      outcome("missing_dates");
      toast({ title: t("booking.select_dates"), description: t("booking.select_dates_desc"), variant: "destructive" });
      return;
    }
    if (!quote) {
      outcome("missing_quote");
      toast({ title: t("common.error"), description: t("booking.wait_for_pricing"), variant: "destructive" });
      return;
    }
    if (!user || !session?.access_token) {
      rememberCheckoutFlowForPostAuthClaim(checkoutFlowIdRef.current);
    }

    clearBwSession(car.id);
    outcome("opened_checkout");
    markCheckoutRouteEntry();
    // localizedHref is a full URL for sign-in handoff. Wouter's inner router
    // already has the market/language base, so bypass base prepending here.
    navigate(`~${buildCheckoutUrl()}`);
    } finally {
      reserveEntryInFlightRef.current = false;
    }
  };

  const pendingBookRef = useRef(false);
  const [pendingBookTick, setPendingBookTick] = useState(0);
  const handleBookRef = useRef(handleBook);
  handleBookRef.current = handleBook;
  useEffect(() => {
    if (!pendingBookRef.current) return;
    if (quoteFresh && !loadingQuote) {
      pendingBookRef.current = false;
      void handleBookRef.current();
    } else if (!loadingQuote && !geocoding && prefillState !== "pending" && (quoteError || deliveryError || serverTimeError || (locationRequiredMissing && !deliveryEditing)
      || (!deliveryEditing && !locationRequiredMissing && lastRequestedQuoteKeyRef.current === currentQuoteKey && !quote))) {
      pendingBookRef.current = false;
    }
  }, [pendingBookTick, currentQuoteKey, quote, quoteFresh, loadingQuote, geocoding, prefillState, quoteError, deliveryError, serverTimeError, locationRequiredMissing, deliveryEditing]);

  const mobileTripReady = !!dateFrom && !!dateTo && !timeRangeInvalid && !timedBookingConflict && !cutoffRejected && !serverTimeError && !(selfDriveUnavailable && activeTab === "driving");
  const mobileLocationReady = isHourlyType ? locationResolved : !deliveryError;
  const mobileStepCanAdvance = mobileStep === "trip" ? mobileTripReady : mobileStep === "location" ? mobileLocationReady : mobileStep === "coverage" ? !loadingQuote : true;
  const [mobileStepAttempted, setMobileStepAttempted] = useState(false);
  const mobileStepBlockedHint = !mobileStepAttempted || mobileStepCanAdvance ? null
    : mobileStep === "trip"
      ? (!dateFrom || !dateTo ? t("booking.select_dates_desc") : null)
      : isHourlyType && !locationResolved ? t("booking.location_required", "Required — enter an address or airport to see your price.") : null;
  const mobileStepTitle = mobileStep === "trip"
    ? t("booking.label_dates")
    : mobileStep === "location"
      ? locationCopy.title
      : mobileStep === "coverage"
        ? t("booking.insurance")
        : t("checkout.price_details");
  const resetMobileScroll = () => { mobileBookingScrollRef.current?.scrollTo?.({ top: 0 }); };
  const goMobileNext = () => {
    if (!mobileStepCanAdvance) {
      setMobileStepAttempted(true);
      if (mobileStep === "location" && isHourlyType) setLocationPromptShown(true);
      return;
    }
    const next = mobileSteps[mobileStepIndex + 1];
    if (!next) return;
    setMobileStepAttempted(false);
    // Hourly trips need a meeting point: open the single editor directly
    // rather than showing a prompt card plus an editor.
    if (next === "location" && isHourlyType && !locationResolved) setDeliveryEditing(true);
    setMobileStep(next);
    resetMobileScroll();
  };
  const goMobileBack = () => {
    const prev = mobileSteps[mobileStepIndex - 1];
    if (!prev) return;
    setMobileStepAttempted(false);
    setMobileStep(prev);
    resetMobileScroll();
  };

  const fieldCardCls = mobile
    ? "bw-mobile-field py-1"
    : "rounded-lg border border-border p-3";
  const fieldLabelCls = mobile
    ? "text-[11px] font-bold uppercase tracking-wider text-muted-foreground"
    : "text-[10px] font-bold uppercase tracking-wider text-muted-foreground";
  const fieldButtonCls = mobile
    ? "flex flex-[1_1_9rem] items-center gap-2.5 rounded-md border border-border px-3.5 py-3 text-left transition-colors hover:bg-muted/50"
    : "flex flex-[1_1_9rem] items-center gap-2 rounded-md border border-border px-3 py-2 text-left transition-colors hover:bg-muted/50";
  const fieldButtonIconCls = mobile
    ? "h-5 w-5 shrink-0 text-muted-foreground"
    : "h-4 w-4 shrink-0 text-muted-foreground";
  const fieldButtonTextCls = cn("whitespace-nowrap", mobile ? "text-base" : "text-sm");
  // Wrap based on actual card space, not viewport breakpoints. Each field
  // keeps a usable basis even in the narrow desktop sidebar.
  const dateTimeRowCls = cn("flex flex-wrap items-center gap-2", mobile ? "mt-2" : "mt-1.5");

  return (
    <div
      data-agent-booking-widget
      className={cn(
        mobile
          ? "flex h-full min-h-0 min-w-0 w-full flex-col bg-background"
          : "rounded-xl border border-border bg-background p-6 shadow-[0_6px_16px_rgba(0,0,0,0.12)]",
      )}
    >
      <div
        ref={mobile ? mobileBookingScrollRef : undefined}
        className={mobile ? "bw-mobile-scroll min-h-0 min-w-0 w-full flex-1 overflow-x-hidden overflow-y-auto scroll-pb-28 px-4 pt-2 pb-4" : "contents"}
        // Mobile only: while the keyboard covers part of the fixed drawer,
        // add that much nested scroll room so the field and its inline
        // suggestions can scroll clear of the Reserve footer.
        style={mobile && keyboardInset > 0 ? { paddingBottom: `calc(1rem + ${keyboardInset}px)` } : undefined}
      >
      {mobile && (
        <div className="bw-step-header" data-testid="booking-mobile-step-header">
          <div className="bw-step-nav">
            {mobileStepIndex > 0 ? (
              <button type="button" className="bw-step-icon" onClick={goMobileBack} aria-label={t("common.back")} data-testid="button-mobile-step-back">
                <ChevronLeft className="h-5 w-5" aria-hidden="true" />
              </button>
            ) : <span className="bw-step-icon-spacer" aria-hidden="true" />}
            <div
              className={cn("bw-step-progress", compactHourlyMode && "invisible")}
              role="progressbar"
              aria-valuemin={1}
              aria-valuemax={mobileSteps.length}
              aria-valuenow={mobileStepIndex + 1}
              aria-label={mobileStepTitle}
            >
              {mobileSteps.map((s, i) => (
                <span key={s} className={cn("bw-step-segment", i <= mobileStepIndex && "is-done")} />
              ))}
            </div>
            {onMobileClose ? (
              <button type="button" className="bw-step-icon" onClick={onMobileClose} aria-label={t("common.close", { defaultValue: "Close" })} data-testid="button-mobile-step-close">
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            ) : <span className="bw-step-icon-spacer" aria-hidden="true" />}
          </div>
          <h2 className={cn("bw-step-title", compactHourlyMode && "!mt-0 !text-lg")} data-testid="text-mobile-step-title">{compactHourlyMode ? t("booking.your_trip", "Your trip") : mobileStepTitle}</h2>
        </div>
      )}
      <div className={stepCls("review")}>
      {/* Task #6806 — "Rare find!" scarcity banner. At most one signal per
          car, resolved server-side from real data (rarity > demand >
          availability) behind the feature_scarcity_banner flag. Brand
          tokens only (no raw hexes; `--accent` is a surface token, so text
          uses text-primary). */}
      {scarcity && !compactHourly ? (() => {
        const iconMap = { rarity: Gem, demand: TrendingUp, availability: CalendarClock } as const;
        const ScarcityIcon = iconMap[scarcity.type];
        const title =
          scarcity.type === "rarity" ? t("carDetail.scarcity_rarity_title")
          : scarcity.type === "demand" ? t("carDetail.scarcity_demand_title")
          : t("carDetail.scarcity_availability_title");
        const desc =
          scarcity.type === "rarity"
            ? t("carDetail.scarcity_rarity_desc", { make: car.make ?? "", model: car.model ?? "" })
            : scarcity.type === "demand"
              ? ((scarcity.recentTripCount ?? 0) >= 2
                  ? t("carDetail.scarcity_demand_desc_trips", { count: scarcity.recentTripCount ?? 0 })
                  : t("carDetail.scarcity_demand_desc_saves", { count: scarcity.favoritesCount ?? 0 }))
              : t("carDetail.scarcity_availability_desc");
        return (
          <div
            className="mb-4 flex items-start gap-2.5 rounded-xl border border-primary/20 bg-primary/5 p-3.5"
            data-testid="scarcity-banner"
            data-scarcity-type={scarcity.type}
          >
            <ScarcityIcon className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
            <div className="text-sm leading-relaxed">
              <p className="font-semibold text-primary">{title}</p>
              <p className="text-muted-foreground">{desc}</p>
            </div>
          </div>
        );
      })() : null}
      {/* Published rates are not a trip quote. Keep them visible while the
          location gate below still prevents calculating or booking a total. */}
      {compactHourly ? null : activeTab === "chauffeured" && price != null && Number.isFinite(Number(price)) && Number(price) > 0 ? (
        <div className="mb-6" data-testid="text-advertised-rate">
          <div className="flex items-baseline gap-1.5">
            <span className="text-[28px] font-semibold leading-none text-foreground tracking-tight">
              {maskPrice(price, currency)}
            </span>
            <span className="text-sm text-muted-foreground">{current?.unit}</span>
          </div>
          {(car.chauffeured_min_price_hourly_rate || car.minimum_price) ? (
            <p className="mt-1.5 text-xs text-muted-foreground">
              {maskPrice(car.chauffeured_min_price_hourly_rate || car.minimum_price || 0, currency)} {t("booking.minimum_charge", "minimum charge")}
            </p>
          ) : null}
        </div>
      ) : activeTab === "event" && ((car.price_event != null && car.price_event > 0) || (price != null && price > 0)) ? (
        <div className="mb-6" data-testid="text-advertised-rate">
          {car.price_event != null && car.price_event > 0 ? (
            <>
              {price != null && price > 0 ? (
                <div className="flex items-baseline gap-1.5">
                  <span className="text-[28px] font-semibold leading-none text-foreground tracking-tight">{maskPrice(price, currency)}</span>
                  <span className="text-sm text-muted-foreground">{t("common.per_hour")}</span>
                </div>
              ) : (
                <div className="flex items-baseline gap-1.5">
                  <span className="text-[28px] font-semibold leading-none text-foreground tracking-tight">{maskPrice(car.price_event, currency)}</span>
                  <span className="text-sm text-muted-foreground">{t("common.per_day")}</span>
                </div>
              )}
              <p className="mt-1.5 text-xs text-muted-foreground">
                {price != null && price > 0 && (
                  <>
                    <span>{maskPrice(car.price_event, currency)}{t("common.per_day")}</span>
                  </>
                )}
                {(car.event_min_price_hourly_rate && car.event_min_price_hourly_rate > 0) ? (
                  <>
                    {price != null && price > 0 && <span className="mx-1.5">·</span>}
                    <span>{maskPrice(car.event_min_price_hourly_rate, currency)} {t("booking.minimum_charge", "minimum charge")}</span>
                  </>
                ) : (car.minimum_price && car.minimum_price > 0) ? (
                  <>
                    {price != null && price > 0 && <span className="mx-1.5">·</span>}
                    <span>{maskPrice(car.minimum_price, currency)} {t("booking.minimum_charge", "minimum charge")}</span>
                  </>
                ) : null}
              </p>
            </>
          ) : price != null && price > 0 ? (
            <>
              <div className="flex items-baseline gap-1.5">
                <span className="text-[28px] font-semibold leading-none text-foreground tracking-tight">{maskPrice(price, currency)}</span>
                <span className="text-sm text-muted-foreground">{current?.unit}</span>
              </div>
              {(car.event_min_price_hourly_rate && car.event_min_price_hourly_rate > 0) ? (
                <p className="mt-1.5 text-xs text-muted-foreground">
                  {maskPrice(car.event_min_price_hourly_rate, currency)} {t("booking.minimum_charge", "minimum charge")}
                </p>
              ) : (car.minimum_price && car.minimum_price > 0) ? (
                <p className="mt-1.5 text-xs text-muted-foreground">
                  {maskPrice(car.minimum_price, currency)} {t("booking.minimum_charge", "minimum charge")}
                </p>
              ) : null}
            </>
          ) : null}
        </div>
      ) : price != null && Number.isFinite(Number(price)) && Number(price) > 0 ? (
        <div className="mb-6">
          <div className="flex items-baseline gap-1.5">
            <span className="text-[28px] font-semibold leading-none text-foreground tracking-tight">{maskPrice(price, currency)}</span>
            <span className="text-sm text-muted-foreground">{current?.unit}</span>
          </div>
        </div>
      ) : null}

      {isHourlyType && !locationResolved && !compactHourly && prefillState !== "pending" ? (
        <div className="mb-6" data-testid="text-price-after-location">
          <p className="text-xs text-muted-foreground">{t("booking.price_after_location_desc", "Add where to meet you below and we'll calculate your exact total.")}</p>
        </div>
      ) : null}

      </div>
      <div className={stepCls("trip")}>
      {/* Task #2342 — the legacy `≈ {converted}` secondary line is
          intentionally absent on browsing surfaces. Renters see prices
          in their preferred currency only; dual display is reserved
          for checkout, where the actual charge currency matters. */}

      {selfDriveUnavailable && car.rental_driving_active && (
        <Alert
          variant="info"
          className="mb-4"
          data-testid={isNewYorkListing ? "ny-self-drive-unavailable" : "self-drive-location-unverified"}
        >
          <AlertDescription>
            {isNewYorkListing
              ? t("booking.ny_self_drive_unavailable", "Self-drive is unavailable for vehicles registered in New York. Choose another rental type.")
              : t("booking.unverified_self_drive_unavailable", "Self-drive availability cannot be confirmed because this vehicle's registration state is unknown.")}
          </AlertDescription>
        </Alert>
      )}

      {tabs.length > 1 ? (
        <div className={cn("flex gap-2", compactHourly ? "mb-3" : "mb-5")}>
          {tabs.map(tab => (
            <button
              key={tab.key}
              type="button"
              aria-pressed={activeTab === tab.key}
              data-testid={`rental-type-${tab.key}`}
              onClick={() => {
                // INP: tab switch re-renders the entire widget; yield to the
                // browser first so the button responds instantly.
                startTransition(() => {
                  setActiveTab(tab.key);
                  onRentalTypeChange?.(tab.key);
                  if (tab.key !== "event") setOccasion("");
                });
              }}
              className={cn(
                "relative flex-1 min-w-0 basis-0 flex flex-col items-center gap-1 rounded-lg border-2 px-1 pb-2 pt-2 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
                activeTab === tab.key
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-border bg-transparent text-muted-foreground hover:border-primary/50 hover:bg-primary/5 hover:text-foreground"
              )}
            >
              {activeTab === tab.key && (
                <span
                  aria-hidden="true"
                  data-testid="rental-type-selected-indicator"
                  className="absolute right-1 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-primary-foreground"
                >
                  <Check className="h-3 w-3" strokeWidth={3} />
                </span>
              )}
              <DsIcon name={tabIconMap[tab.key]} className={compactHourly ? "h-5 w-5" : "h-6 w-6"} />
              {tab.label}
            </button>
          ))}
        </div>
      ) : tabs.length === 1 ? (
        <div className="mb-5 flex justify-center border-b border-border">
          <div className="flex flex-col items-center gap-1 pb-2.5 pt-1 text-xs font-semibold text-primary border-b-2 border-primary -mb-px">
            <DsIcon name={tabIconMap[tabs[0].key]} className="h-6 w-6" />
            {tabs[0].label}
          </div>
        </div>
      ) : null}

      {/* Task #878: Inline notice that replaces the previous event-rental
          modal's step-1 banner. Shown on the Events tab so renters know
          before clicking Reserve that an event rental does not include
          driving, with a one-click link to switch to Driving when that
          rental type is enabled for this listing. */}
      {activeTab === "event" && (() => {
        const drivingTab = tabs.find(tt => tt.key === "driving");
        return (
          <div
            className={cn("flex items-start gap-2 rounded-xl bg-sky-50 text-sky-900 dark:bg-sky-950/40 dark:text-sky-100", compactHourly ? "mb-3 p-2.5 text-xs" : "mb-4 p-3.5 text-sm")}
            data-testid="event-no-driving-notice"
          >
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-sky-600 dark:text-sky-300" />
            <p className="leading-relaxed">
              {t(
                "booking.event_no_driving_notice",
                "Event rentals do not include driving. The vehicle is delivered to the location."
              )}
              {drivingTab && (
                <>
                  {" "}
                  {t(
                    "booking.event_no_driving_switch_prefix",
                    "If you need to drive the vehicle during any rental, book a"
                  )}{" "}
                  <button
                    type="button"
                    onClick={() => {
                      startTransition(() => {
                        setActiveTab("driving");
                        onRentalTypeChange?.("driving");
                        setOccasion("");
                      });
                    }}
                    className="font-semibold text-sky-700 underline underline-offset-2 hover:text-sky-900 dark:text-sky-200"
                    data-testid="event-switch-to-driving"
                  >
                    {t("booking.event_no_driving_switch_link", "Driving rental")}
                  </button>
                  .
                </>
              )}
            </p>
          </div>
        );
      })()}

      <Popover open={datePopupOpen} onOpenChange={setDatePopupOpen}>
        <PopoverAnchor asChild>
          <div className={mobile ? "bw-mobile-trip space-y-3.5" : "space-y-3"} role={mobile ? "group" : undefined} aria-label={mobile ? t("checkout.dates_label") : undefined}>
            {activeTab === "event" ? (
              <EventTripFields
                dateFrom={dateFrom}
                dateTo={dateTo}
                pickupTime={pickupTime}
                dropoffTime={dropoffTime}
                twelveHour={twelveHourClock}
                availability={availability}
                mobile={mobile}
                onDateChange={(field, d) => {
                  const from = field === "start" ? d : dateFrom;
                  let to = field === "end" ? d : dateTo;
                  let endTime = dropoffTime;
                  // Keep an existing valid end. Seed a missing/outdated end
                  // only when the start moves beyond it, never truncate a range.
                  if (field === "start" && (!to || to < from)) {
                    const end = deriveHourlyEnd(from, pickupTime, hourlyHours);
                    to = end?.dateTo ?? from;
                    if (end) {
                      endTime = end.dropoffTime;
                      setDropoffTime(endTime);
                    }
                  }
                  setDateFrom(from);
                  setDateTo(to);
                  onDateSelect?.(from, to);
                  trackBookingBox("booking_date_selected", car.id, activeTab, { field });
                  const hours = elapsedHours(from, pickupTime, to, endTime);
                  if (hours != null) trackBookingBox("booking_duration_selected", car.id, activeTab, { hours });
                }}
                onTimeChange={(field, time) => {
                  if (field === "start") setPickupTime(time);
                  else setDropoffTime(time);
                  const hours = elapsedHours(dateFrom, field === "start" ? time : pickupTime,
                    dateTo, field === "end" ? time : dropoffTime);
                  if (hours != null) trackBookingBox("booking_duration_selected", car.id, activeTab, { hours });
                }}
              />
            ) : activeTab === "chauffeured" ? (
              <HourlyTripFields
                date={dateFrom}
                startTime={pickupTime}
                hours={hourlyHours}
                durationOptions={hourlyOptions}
                twelveHour={twelveHourClock}
                availability={availability}
                mobile={mobile}
                endLabel={hourlyEndLabel}
                onDateChange={(d) => {
                  setDateFrom(d);
                  const end = deriveHourlyEnd(d, pickupTime, hourlyHours);
                  const to = end?.dateTo ?? d;
                  if (end) setDropoffTime(end.dropoffTime);
                  setDateTo(to);
                  onDateSelect?.(d, to);
                  trackBookingBox("booking_date_selected", car.id, activeTab);
                }}
                onStartChange={setPickupTime}
                onHoursChange={(h) => {
                  setHourlyHours(h);
                  trackBookingBox("booking_duration_selected", car.id, activeTab, { hours: h });
                }}
              />
            ) : (<>
            <div className={fieldCardCls}>
              <p className={fieldLabelCls}>{t("booking.pick_up")}</p>
              <div className={dateTimeRowCls}>
                <button
                  type="button"
                  onClick={handlePickupClick}
                  className={cn(
                    fieldButtonCls,
                    canOpenDatePopup && datePopupOpen && datePopupActiveField === "start" && "border-foreground ring-1 ring-foreground"
                  )}
                  data-testid="booking-date-pickup"
                >
                  <Calendar className={fieldButtonIconCls} />
                  {dateFrom ? (
                    <span className={cn(fieldButtonTextCls, "text-foreground")}>{formatDisplayDate(dateFrom)}</span>
                  ) : (
                    <span className={cn(fieldButtonTextCls, "text-muted-foreground")}>{t("booking.select_date", "Select date")}</span>
                  )}
                </button>
                <div className="min-w-max flex-[1_1_9rem]">
                  <TimePicker label={t("booking.pick_up", "Pick-up")} value={pickupTime} onChange={setPickupTime} onCommit={commitTimeSelection} size={mobile ? "lg" : "sm"} />
                </div>
              </div>
            </div>

            <div className={fieldCardCls}>
              <p className={fieldLabelCls}>{t("booking.drop_off")}</p>
              <div className={dateTimeRowCls}>
                <button
                  type="button"
                  onClick={handleDropoffClick}
                  className={cn(
                    fieldButtonCls,
                    canOpenDatePopup && datePopupOpen && datePopupActiveField === "end" && "border-foreground ring-1 ring-foreground"
                  )}
                  data-testid="booking-date-dropoff"
                >
                  <Calendar className={fieldButtonIconCls} />
                  {dateTo ? (
                    <span className={cn(fieldButtonTextCls, "text-foreground")}>{formatDisplayDate(dateTo)}</span>
                  ) : (
                    <span className={cn(fieldButtonTextCls, "text-muted-foreground")}>{t("booking.select_date", "Select date")}</span>
                  )}
                </button>
                <div className="min-w-max flex-[1_1_9rem]">
                  <TimePicker label={t("booking.drop_off", "Drop-off")} value={dropoffTime} onChange={setDropoffTime} onCommit={commitTimeSelection} size={mobile ? "lg" : "sm"} />
                </div>
              </div>
            </div>
            </>)}

            {(timeRangeInvalid || serverTimeError) && (
              <p
                ref={timeRangeErrorRef}
                className="px-1 text-xs text-destructive"
                role="alert"
                tabIndex={-1}
                data-testid="booking-time-range-error"
              >
                {serverTimeError?.message || (timeIssue?.reason === "minimum_notice"
                  ? t("booking.notice_time_error", "This car needs more notice. Choose pickup on or after {{date}} (car's local date).", { date: timeIssue.earliestDate })
                  : timeIssue?.reason === "chauffeured_same_day_window"
                    ? t("booking.chauffeur_time_error", "Return ({{end}}) must be after pickup ({{start}}) on the same day, allowing at least one hour. 12 am is midnight; 12 pm is noon.", { start: pickupTime, end: dropoffTime })
                    : timeIssue?.reason === "minimum_hourly_duration"
                      ? t("booking.minimum_hour_error", "Allow at least one hour between pickup and return.")
                      : timeIssue?.reason === "invalid_time_format"
                        ? t("booking.time_format_error", "Enter a valid pickup and return time, for example 10:00 am or 14:00.")
                        : timeIssue?.reason === "invalid_date_format"
                          ? t("booking.date_format_error", "Choose valid pickup and return dates.")
                          : t("booking.time_order_error", "Return must be after pickup. For an overnight trip, choose the following return date."))}
                {timeIssue?.correction && (
                  <button type="button" className="mt-1 block font-medium underline"
                    data-testid="booking-correct-time"
                    onClick={() => {
                      const correction = timeIssue.correction!;
                      setDateTo(correction.dateTo);
                      setDropoffTime(correction.dropoffTime);
                      onDateSelect?.(dateFrom, correction.dateTo);
                    }}>
                    {t("booking.correct_time", "Use {{date}} at {{time}}", { date: timeIssue.correction.dateTo, time: timeIssue.correction.dropoffTime })}
                  </button>
                )}
              </p>
            )}
            {timedBookingConflict && (
              <p
                ref={timedConflictErrorRef}
                className="px-1 text-xs text-destructive"
                role="alert"
                tabIndex={-1}
                data-testid="booking-timed-interval-error"
              >
                {t("booking.timed_booking_conflict", "Another booking overlaps the selected time or the required one-hour preparation/turnaround buffer. Please choose a different time.")}
              </p>
            )}

            {/* Task #3972 (F.12) — clarify that the pick-up / drop-off times
                above are the CAR's local wall-clock, not the renter's browser
                timezone. Shown for the Event Rental step where the host-side
                local time matters most. Hidden when we can't confidently
                derive a zone from the car's location data. */}
            {carTimezone && !compactHourly && (
              <p className="px-1 text-xs text-muted-foreground" data-testid="booking-timezone-note">
                {t("booking.times_in_car_timezone", "Times shown in the car's local time ({{tz}})", { tz: carTimezone })}
              </p>
            )}
          </div>
        </PopoverAnchor>
        {canOpenDatePopup && !isHourlyType && (
          <PopoverContent
            side="left"
            align="start"
            sideOffset={16}
            collisionPadding={16}
            className="w-auto rounded-3xl border border-border/70 bg-background p-0 shadow-[0_24px_60px_-20px_rgba(15,23,42,0.25),0_8px_24px_-12px_rgba(15,23,42,0.15)]"
            data-testid="booking-date-popup"
          >
            <DateRangePopup
              monthsToShow={isNarrow ? 1 : 2}
              availability={availability!}
              selectedFrom={dateFrom || externalFrom || ""}
              selectedTo={dateTo || externalTo || ""}
              rentalType={activeTab}
              initialActiveField={datePopupActiveField}
              onChange={(from, to) => {
                onDateSelect?.(from, to);
                if (from) trackBookingBox("booking_date_selected", car.id, activeTab);
              }}
              onClose={() => setDatePopupOpen(false)}
            />
          </PopoverContent>
        )}
      </Popover>

      </div>
      <div className={stepCls(deliveryAllowedForTab ? "location" : "review")}>
      {(car.city || car.state || isHourlyType) && (
        <div className={cn("mt-3 space-y-2", mobile && "bw-mobile-delivery")} role={mobile ? "group" : undefined} aria-label={mobile ? locationCopy.title : undefined}>
          {isHourlyType && !compactHourly && (
            <p className="text-sm leading-snug text-muted-foreground" data-testid="text-pickup-location-help">
              {locationCopy.help}
            </p>
          )}
          {(
            <div className={cn(mobile && deliveryEditing && deliveryAllowedForTab && "hidden")}>
              <div className="flex items-start gap-2.5 rounded-lg border border-border px-3 py-2.5">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{locationCopy.title}</p>
                  {deliveryAddress ? (
                    <div className="flex items-center gap-1">
                      {deliveryAirportCode && <Plane className="h-3 w-3 shrink-0 text-primary" aria-hidden="true" />}
                      <p className="text-sm text-foreground truncate" title={deliveryAddress}>{deliveryAddress}</p>
                    </div>
                  ) : (
                    <p className="text-sm font-medium text-foreground break-words">
                      {isHourlyType
                        ? locationCopy.prompt
                        : [car.city, car.state].filter(Boolean).join(", ")}
                    </p>
                  )}
                  {showingPrefill ? (
                    <p className="mt-0.5 text-xs leading-snug text-muted-foreground" data-testid="text-location-prefill-caption">
                      {activeTab === "event"
                        ? t("booking.price_for_delivery_in_city", "Price for delivery in {{city}}.", { city: prefillCity })
                        : t("booking.price_for_pickup_in_city", "Price for pickup in {{city}}.", { city: prefillCity })}{" "}
                      <button type="button" className="font-semibold text-foreground underline underline-offset-2" data-testid="button-change-location"
                        onClick={() => { setDeliveryEditing(true); requestAnimationFrame(() => deliveryInputRef.current?.focus()); }}>
                        {t("booking.change_location", "Change location")}
                      </button>
                    </p>
                  ) : null}
                  {isHourlyType && !locationResolved && prefillState === "pending" ? (
                    <div className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground" role="status" data-testid="text-location-prefill-pending">
                      <Spinner size="sm" />
                      {t("booking.prefill_pending", "Finding {{city}} centre to show your price…", { city: prefillCity })}
                    </div>
                  ) : null}
                  {isHourlyType && !locationResolved && prefillState === "error" && !deliveryEditing ? (
                    <p className="mt-0.5 text-xs font-medium text-destructive" role="alert" data-testid="text-location-prefill-error">
                      {t("booking.prefill_error", "We couldn't look up {{city}} right now. Enter a meeting place to see your price.", { city: prefillCity || car.country_code || "" })}
                    </p>
                  ) : null}
                  {isHourlyType && !locationResolved && prefillState !== "pending" ? (
                    <p
                      className={`mt-0.5 text-xs leading-snug ${locationPromptShown ? "font-medium text-destructive" : "text-muted-foreground"}`}
                      data-testid="text-location-required"
                      role={locationPromptShown ? "alert" : undefined}
                    >
                      {t("booking.location_required", "Required — enter an address or airport to see your price.")}
                    </p>
                  ) : null}
                  {activeTab === "driving" && deliveryAllowedForTab && !deliveryAddress ? (
                    <p className="mt-0.5 text-xs leading-snug text-muted-foreground" data-testid="text-delivery-optional">
                      {t("booking.delivery_optional_widget", "Collect from the car, or have it delivered to you.")}
                    </p>
                  ) : null}
                  {deliveryAllowedForTab && !deliveryEditing && (isHourlyType ? !locationResolved : !deliveryAddress) ? (
                    <button
                      type="button"
                      data-testid="button-open-location-editor"
                      aria-label={t("booking.set_delivery_aria", isHourlyType ? "Enter location" : "Deliver to me")}
                      onClick={() => setDeliveryEditing(true)}
                      className="mt-1.5 inline-flex min-h-9 items-center rounded-md border border-border px-2.5 text-xs font-semibold text-foreground hover:bg-muted/60 transition-colors"
                    >
                      {isHourlyType ? t("booking.enter_location", "Enter location") : t("booking.choose_delivery", "Deliver to me")}
                    </button>
                  ) : null}
                </div>
                {deliveryAllowedForTab && deliveryAddress ? (
                  <button
                    type="button"
                    onClick={clearDeliveryLocation}
                    className="-mr-1 inline-flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full hover:bg-muted transition-colors"
                    aria-label={t("booking.clear_delivery_aria")}
                  >
                    <X className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  </button>
                ) : null}
              </div>
            </div>
          )}

          {deliveryEditing && deliveryAllowedForTab && (
            <div className="rounded-lg border border-border p-3 space-y-2">
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                {isHourlyType ? t("booking.pickup_location") : t("booking.delivery_address", "Enter address")}
              </p>
              <div className="relative">
                <div className="flex gap-2">
                  <input
                    ref={deliveryInputRef}
                    type="text"
                    autoFocus={deliveryEditing}
                    value={deliveryInputValue}
                    onChange={(e) => {
                      locationEpochRef.current += 1;
                      setDeliveryInputValue(e.target.value);
                      fetchAddressSuggestions(e.target.value);
                    }}
                    placeholder={isHourlyType ? t("booking.pickup_location_placeholder", "Enter a place or address") : t("booking.delivery_placeholder", "Enter location")}
                    className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-base md:text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        setShowSuggestions(false);
                        void handleDeliveryGeocode(e.currentTarget.value, "enter");
                      }
                      if (e.key === "Escape") {
                        if (showSuggestions) {
                          setShowSuggestions(false);
                        } else {
                          setDeliveryEditing(false);
                        }
                      }
                    }}
                    onFocus={() => setDeliveryInputFocused(true)}
                    onBlur={() => {
                      setDeliveryInputFocused(false);
                      // Mobile inline suggestions stay in flow after the
                      // keyboard closes (a touch tap on More results blurs
                      // first); they close on select, Escape or new query.
                      if (!mobile) setTimeout(() => setShowSuggestions(false), 200);
                    }}
                    disabled={geocoding}
                    enterKeyHint={isHourlyType ? "done" : undefined}
                    aria-autocomplete="list"
                    aria-controls="booking-delivery-location-suggestions"
                    aria-expanded={showSuggestions && addressSuggestions.length > 0}
                  />
                  {isHourlyType ? (geocoding ? <span className="inline-flex items-center px-1" role="status" aria-label={t("common.loading", { defaultValue: "Loading" })}><Spinner size="sm" /></span> : null) : (
                  <button
                    type="button"
                    onClick={() => {
                      setShowSuggestions(false);
                      handleDeliveryGeocode(deliveryInputValue);
                    }}
                    disabled={geocoding || !deliveryInputValue.trim()}
                    className="rounded-md bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50 transition-opacity"
                  >
                    {geocoding ? <Spinner size="sm" /> : t("common.go", "Go")}
                  </button>
                  )}
                </div>
                <MobileAwareAutocomplete
                  anchorRef={deliveryInputRef}
                  open={showSuggestions}
                  items={addressSuggestions}
                  listId="booking-delivery-location-suggestions"
                  portalMarker="booking-delivery-location"
                  portalOnMobile={false}
                  inlineLimit={mobile ? 3 : undefined}
                  getKey={(suggestion, index) =>
                    `${suggestion.lat}:${suggestion.lon}:${suggestion.airport_code ?? ""}:${index}`
                  }
                  onSelect={(suggestion) => {
                    locationEpochRef.current += 1;
                    void handleDeliverySelect(
                      suggestion.display_name,
                      parseFloat(suggestion.lat),
                      parseFloat(suggestion.lon),
                      suggestion.airport_code,
                      { source: "autocomplete" },
                    );
                  }}
                  renderItem={(suggestion) => {
                    if (!mobile) return <span className="line-clamp-2">{suggestion.display_name}</span>;
                    const lines = addressSuggestionLines(suggestion);
                    return (
                      <span className="flex min-w-0 flex-col">
                        <span className="truncate font-medium">{lines.primary}</span>
                        {lines.secondary ? <span className="truncate text-sm text-muted-foreground">{lines.secondary}</span> : null}
                      </span>
                    );
                  }}
                />
              </div>
              {deliveryAirports.length > 0 && (
                <div className="space-y-1">
                  <p className="text-[10px] text-muted-foreground">{t("booking.or_select_airport", "Or select an airport:")}</p>
                  {deliveryAirports.map(ap => (
                    <button
                      key={ap.airport_code}
                      type="button"
                      onClick={() => {
                        locationEpochRef.current += 1;
                        trackBookingBox("location_changed", car.id, activeTab, { source: "airport" });
                        setDeliveryAirportCode(ap.airport_code);
                        setDeliveryDistance(null);
                        setDeliveryLatitude(null);
                        setDeliveryLongitude(null);
                        setDeliveryAddress(`${ap.airport_name} (${ap.airport_code})`);
                        setDeliveryError(null);
                        setDeliveryEditing(false);
                        setDeliveryInputValue("");
                      }}
                      className="flex w-full items-center justify-between rounded-md border border-border px-3 py-2 text-left text-sm hover:bg-muted/50 transition-colors"
                    >
                      <span className="flex items-center gap-2">
                        <Plane className="h-3.5 w-3.5 text-muted-foreground" />
                        <span>{ap.airport_name}</span>
                        <span className="text-xs text-muted-foreground">({ap.airport_code})</span>
                      </span>
                      <span className="text-xs font-medium text-primary">{maskPrice(ap.flat_fee, currency)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {deliveryError && (
            <Alert
              ref={deliveryErrorRef}
              variant="warning"
              role="alert"
              tabIndex={-1}
              data-testid="booking-delivery-error"
            >
              <AlertDescription className="text-xs">{deliveryError}</AlertDescription>
            </Alert>
          )}

          {deliveryDistance != null && deliveryDistance > 0 && !deliveryAirportCode && (
            <p className="text-xs text-muted-foreground px-1">
              {t("booking.delivery_distance", "Delivery distance: {{miles}} {{unit}}", { miles: deliveryDistance, unit: getDistanceUnitPlural(car.country_code) })}
            </p>
          )}
        </div>
      )}

      </div>
      <div className={stepCls("trip")}>
      {activeTab === "chauffeured" && (
        <div className="mt-3 rounded-lg border border-border p-3">
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{t("booking.num_passengers", "Number of Passengers")}</p>
          <div className="mt-1.5 relative">
            <select
              aria-label={t("booking.num_passengers", "Number of Passengers")}
              value={numPassengers}
              onChange={e => setNumPassengers(Number(e.target.value))}
              className="w-full min-h-11 appearance-none rounded-md border border-border bg-background px-3 py-2 pr-8 text-base md:text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            >
              {Array.from({ length: Math.max((car.passengers ?? 2) - 1, 1) }, (_, i) => i + 1).map(n => (
                <option key={n} value={n}>{n} {n === 1 ? t("booking.passenger", "Passenger") : t("booking.passengers", "Passengers")}</option>
              ))}
            </select>
            <Users className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          </div>
        </div>
      )}

      </div>
      <div className={stepCls("review")}>
      {mobile && !compactHourly && (
        <section className="bw-review-trip" data-testid="booking-mobile-trip-review">
          <p className="text-base font-semibold">{[car.year, car.make, car.model].filter(Boolean).join(" ")}</p>
          <p className="mt-1 text-sm text-muted-foreground">{current?.label}</p>
          <dl className="mt-4 space-y-3 text-sm">
            <div className="flex items-start justify-between gap-4">
              <dt className="text-muted-foreground">{t("booking.pick_up")}</dt>
              <dd className="text-right font-medium">{formatDisplayDate(dateFrom)} · {pickupTime}</dd>
            </div>
            <div className="flex items-start justify-between gap-4">
              <dt className="text-muted-foreground">{t("booking.drop_off")}</dt>
              <dd className="text-right font-medium">{formatDisplayDate(dateTo)} · {dropoffTime}</dd>
            </div>
            {(deliveryAddress || car.city || car.state) && (
              <div className="flex items-start justify-between gap-4">
                <dt className="shrink-0 text-muted-foreground">{t("booking.location")}</dt>
                <dd className="min-w-0 break-words text-right font-medium">{deliveryAddress || [car.city, car.state].filter(Boolean).join(", ")}</dd>
              </div>
            )}
          </dl>
        </section>
      )}
      {/* Task #3945 — reduced-motion users keep the classic first-load
          spinner; motion users instead see the total churn (rendered by the
          block below) in place of this indicator. */}
      {loadingQuote && !quote && reducedMotion && (
        <div className="mt-3 flex items-center justify-center gap-2 py-2 text-sm text-muted-foreground">
          <Spinner size="sm" />
          {t("common.calculating")}
        </div>
      )}

      {quoteError && !serverTimeError && !loadingQuote && !quote && (
        <div
          className="mt-3 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2"
          role="alert"
        >
          <p className="text-xs text-destructive">{quoteError}</p>
        </div>
      )}

      {(quote || (loadingQuote && !reducedMotion)) && !isPreview && (() => {
        // Task #3945 — first load (no quote yet): churn the total in place of
        // the old "Calculating…" spinner. The stable `key="bw-total"` row is
        // reused once the quote/breakdown render below, so this same
        // RollingNumber instance rolls → settles smoothly onto the first price.
        if (!quote) {
          const fmtPending = (n: number) => formatLocalisedMoney(n, currency);
          return (
            <div className="mt-4 space-y-2 rounded-xl border border-border bg-card p-4">
              <div key="bw-total" className="flex items-center justify-between pt-0.5">
                <span className="text-sm font-bold text-primary">{t("booking.total")}</span>
                {/* Keep this row's inner structure identical to the loaded
                    total row below so React reuses the SAME RollingNumber
                    instance across the no-quote→quote transition (smooth
                    churn→settle). The reduced-motion spinner never shows in this
                    branch (it only renders for motion users), but the wrapping
                    span must match. */}
                <span className="inline-flex items-center gap-1.5">
                  {loadingQuote && reducedMotion && <Spinner size="sm" />}
                  <RollingNumber
                    className="text-sm font-bold text-primary"
                    data-testid="widget-total-amount"
                    value={null}
                    loading={loadingQuote}
                    format={fmtPending}
                  />
                </span>
              </div>
            </div>
          );
        }
        const sym = getCurrencySymbol(currency);
        const breakdown = quote.dailyRateBreakdown || [];
        // Source-of-truth for the rental-fee line is the backend's
        // `rentalFeeBilling` signal (task #826). Older quote payloads may not
        // include it yet (transient state during a deploy where the front-end
        // is updated before the API), so derive the same modes locally from
        // the existing fields. Critically, this fallback also catches the
        // bug-report case (event tab with no hourly rate → daily-flat) so
        // mixed deployments don't regress to "$X/hour × Yh".
        const billing = quote.rentalFeeBilling
          ?? (breakdown.length > 0
            ? "daily-custom"
            : activeTab === "event"
              ? (quote.eventDailyCapped
                  ? "daily-capped"
                  : (car.event_hourly_rate && car.event_hourly_rate > 0
                      ? "hourly"
                      : "daily-flat"))
              : isHourlyType
                ? "hourly"
                : "daily");
        const hasCustomRates = billing === "daily-custom";

        let rateGroups: Array<{ rate: number; dates: string[] }> = [];
        if (hasCustomRates) {
          for (const entry of breakdown) {
            const last = rateGroups[rateGroups.length - 1];
            if (last && last.rate === entry.rate) {
              last.dates.push(entry.date);
            } else {
              rateGroups.push({ rate: entry.rate, dates: [entry.date] });
            }
          }
        }

        const formatShortDate = (d: string) => {
          return formatDate(d, { month: "short", day: "numeric" });
        };

        // Task #4034 — all fees shown inline, nothing folded.
        const widgetTotalDue = Math.round(quote.paymentDue * 100) / 100;
        const deliveryEngaged = deliveryAllowedForTab && (!!deliveryAirportCode || (!!deliveryAddress && deliveryDistance != null));
        const allLines = buildPriceBreakdownLines(quote, {
          canonicalTotal: widgetTotalDue,
          includeZeroDelivery: deliveryEngaged,
        });
        // When custom per-day rate groups are shown above (hasCustomRates), each
        // date-group row already accounts for the rental charge. Suppress the
        // aggregate rental_fee line so it isn't displayed twice.
        const lines = hasCustomRates ? allLines.filter(l => l.key !== "rental_fee") : allLines;
        // Task #2444 — render every price string through the
        // market-aware formatter so DK/DE/SE visitors see the
        // correct thousands / decimal separators (`24.000 kr`,
        // `24.000 €`, `24 000 kr`) instead of the historical
        // hardcoded `${sym}${n.toFixed(2)}` (which always rendered
        // a `.` decimal and a `,` thousands separator).
        const fmtRate = (n: number) => formatLocalisedMoney(n, currency, { decimals: n % 1 === 0 ? 0 : 2 });
        const fmtAmt = (n: number) => formatLocalisedMoney(n, currency);

        return (
          <div className="mt-4 space-y-2 rounded-xl border border-border bg-card p-4">
            {/* Task #3945 — while the total churns, soften the line-item
                breakdown above it (blur + fade) so the eye lands on the
                rolling figure instead of the stale sub-totals. Skipped for
                reduced-motion users (they already see plain values). */}
            <div
              key="bw-breakdown"
              className={cn(
                "space-y-2 transition-[filter,opacity] duration-300",
                loadingQuote && !reducedMotion && "blur-[2px] opacity-50",
              )}
            >
            {hasCustomRates && (
              <div className="space-y-1.5">
                {rateGroups.map((group, i) => {
                  const startDate = formatShortDate(group.dates[0]);
                  const endDate = group.dates.length > 1 ? formatShortDate(group.dates[group.dates.length - 1]) : null;
                  const label = endDate ? `${startDate}–${endDate}` : startDate;
                  const isCustom = group.rate !== quote.baseRate;
                  return (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <span className={cn("text-muted-foreground", isCustom && "font-medium text-amber-600 dark:text-amber-400")}>
                        {fmtRate(group.rate)}/day × {group.dates.length}d ({label})
                      </span>
                      <span className="text-muted-foreground">{fmtAmt(group.rate * group.dates.length)}</span>
                    </div>
                  );
                })}
              </div>
            )}
            <PriceBreakdownLines lines={lines} fmt={fmtAmt} />
            </div>
            <div key="bw-total" className="flex items-center justify-between border-t border-border pt-2.5">
              <span className="text-sm font-bold text-primary">{t("booking.total")}</span>
              {/* Task #3945 — reduced-motion users don't get the churn, so during
                  a recalc (quote already on screen) surface a small inline
                  spinner beside the plain, updating total. Motion users see the
                  odometer churn instead. The span wrapper mirrors the first-load
                  row so the RollingNumber instance is preserved no-quote→quote. */}
              <span className="inline-flex items-center gap-1.5">
                {loadingQuote && reducedMotion && <Spinner size="sm" />}
                <RollingNumber
                  className="text-sm font-bold text-primary"
                  data-testid="widget-total-amount"
                  value={widgetTotalDue}
                  loading={loadingQuote}
                  format={fmtAmt}
                />
              </span>
            </div>
            {/* Task #3851 (H8): the headline price renders in the renter's
                preferred currency, but the rolled-up quote total above is shown
                in the listing's local currency (`currency`). When those differ
                the renter sees two different currencies with no explanation, so
                disclose that the booking is charged in the listing currency and
                show the approximate preferred-currency equivalent. */}
            {preferredCurrency !== currency && (() => {
              const dual = chargeDisclosure(widgetTotalDue, currency);
              if (!dual) return null;
              return (
                <p className="text-xs text-muted-foreground" data-testid="widget-fx-disclosure">
                  {t("booking.charged_in_currency", { defaultValue: "Charged in {{currency}}", currency })} · ≈ {dual.preferred}
                </p>
              );
            })()}
            {/* Insurance modal trigger preserved as a quiet inline link
                so renters can still inspect coverage without unfolding the
                rolled-up rental row. */}
            {isInsurable && (
              <button
                type="button"
                data-testid="button-desktop-coverage-change"
                onClick={() => setInsuranceModalOpen(true)}
                className="ml-3 inline-flex items-center gap-1 text-xs text-muted-foreground underline decoration-dotted underline-offset-2"
              >
                {t("booking.insurance")}
                {quote.insurancePackageName ? ` (${quote.insurancePackageName})` : ""}
              </button>
            )}
            {/* Task #853: refundable deposits are scoped to self-drive only — never
                surface a deposit row for chauffeur or event quotes (the backend
                returns 0 for those, but we also gate on activeTab as a UI-side
                guarantee in case stale state lingers across tab switches). */}
            {activeTab === "driving" && quote.deposit > 0 && (
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>{t("booking.refundable_deposit")}</span>
                <span>{fmtAmt(quote.deposit)}</span>
              </div>
            )}
          </div>
        );
      })()}

      </div>
      <div className={stepCls("trip")}>
      {/* Legacy in-widget occasion picker. Gated on the Event rental tab
          AND a non-empty list of host-enabled event types for this listing.
          The per-listing event_types config was removed from the listing
          wizard, so `enabled` is effectively always empty and this block
          short-circuits to null — the live occasion picker now lives on
          the Confirm-and-pay page. Left in place as a no-op fallback in
          case a legacy listing still surfaces eventTypes from the API. */}
      {activeTab === "event" && (() => {
        const enabled = (eventTypes || [])
          .map(et => ({ slug: String(et.slug || "").trim(), name: String(et.name || "").trim() }))
          .filter(et => !!et.slug);
        if (enabled.length === 0) return null;
        return (
          <div className="mt-4 rounded-xl border border-border bg-card p-3.5">
            <Label htmlFor="bw-occasion" className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              {t("booking.occasion_label", "Occasion (optional)")}
            </Label>
            <Select value={occasion} onValueChange={(v) => setOccasion(v === "__none" ? "" : v)}>
              <SelectTrigger id="bw-occasion" className="mt-1.5 h-11" data-testid="select-occasion">
                <SelectValue placeholder={t("booking.occasion_placeholder", "Tell your host what it's for")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">{t("booking.occasion_none", "No preference")}</SelectItem>
                {enabled.map((et) => {
                  const known = ALL_OCCASIONS[et.slug];
                  return (
                    <SelectItem key={et.slug} value={et.slug}>{known?.label || et.name || et.slug}</SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
        );
      })()}

      </div>
      {mobile && isInsurable && !compactHourly && (
        <div className={stepCls("coverage")} data-testid="booking-mobile-coverage-step">
          {loadingQuote && !quote ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground"><Spinner size="sm" /> {t("common.loading", { defaultValue: "Loading" })}</div>
          ) : quote && Number.isFinite(quote.insuranceFee) && quote.insuranceFee >= 0 ? (
            <div className="bw-coverage-row">
              <div className="min-w-0">
                <p className="text-base font-semibold text-foreground">{quote.insurancePackageName || t("booking.insurance")}</p>
                <p className="mt-0.5 text-sm text-muted-foreground">{maskPrice(quote.insuranceFee, currency)}</p>
              </div>
              <button
                type="button"
                onClick={() => setInsuranceModalOpen(true)}
                className="bw-coverage-change"
                aria-label={`${t("common.change")} ${t("booking.insurance")}`}
                data-testid="button-mobile-coverage-change"
              >
                {t("common.change")}
              </button>
            </div>
          ) : quoteError ? (
            <p className="text-sm text-destructive" role="alert">{quoteError}</p>
          ) : null}
        </div>
      )}
      <div className={stepCls("review")}>
      {mobile && activeTab === "driving" && isInsurable && (
        <div className="bw-mobile-coverage mt-4 flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
          <Shield className="h-3.5 w-3.5" aria-hidden="true" />
          <span>{t("common.protected_by_insurance")}</span>
        </div>
      )}
      </div>

      </div>

      <div
        ref={mobile ? mobileFooterRef : undefined}
        className={
          mobile
            ? "bw-mobile-footer border-t border-border bg-background px-4 pt-3 pb-[max(0.875rem,env(safe-area-inset-bottom))]"
            : "contents"
        }
      >
      {mobile && mobileStep !== "review" ? (
        <div className="bw-step-footer">
          {mobileStepBlockedHint ? (
            <p className="bw-step-hint" role="status" data-testid="text-mobile-step-hint">{mobileStepBlockedHint}</p>
          ) : null}
          <button
            type="button"
            className="primary-action bw-step-next"
            onClick={goMobileNext}
            aria-disabled={!mobileStepCanAdvance}
            data-testid="button-mobile-step-next"
          >
            {t("common.next")}
          </button>
        </div>
      ) : isPreview ? (
        <div
          className={cn(
            "w-full rounded-full bg-muted text-center font-semibold text-muted-foreground",
            mobile ? "py-3.5 text-base" : "mt-5 py-3 text-sm",
          )}
        >
          {t("car.preview_mode")}
        </div>
      ) : (
        (() => {
          // Task #6387 — Instant Book emphasis at the CTA. The page-level
          // badge may key off `car.instant_rental` + flag, but everything at
          // or near the CTA follows the server quote's `instantBooking` once
          // a quote exists: it respects the `feature_instant_book`
          // kill-switch AND per-date windows for `instant_book_mode=selected`
          // cars, so we never promise an instant confirmation the quote
          // didn't grant. Before a quote exists (no dates picked yet) the
          // lightning only shows for cars that are instant on ALL dates.
          const carInstant = instantBookEnabled && !!car.instant_rental;
          const selectedDatesMode = car.instant_book_mode === "selected";
          const ctaInstant = quote ? quote.instantBooking : (carInstant && !selectedDatesMode);
          return (
            <>
              <button
                className={cn(
                  "primary-action w-full rounded-full font-semibold transition-opacity disabled:opacity-50",
                  mobile ? "py-3.5 text-base" : "mt-5 py-3 text-sm",
                )}
                onClick={handleBook}
                aria-busy={authLoading || undefined}
                disabled={authLoading || booking || loadingQuote || cutoffRejected || (selfDriveUnavailable && activeTab === "driving")}
              >
                {booking ? (
                  <span className="flex items-center justify-center gap-2">
                    <Spinner size="sm" />
                    {t("common.processing")}
                  </span>
                ) : ctaInstant ? (
                  <span className="flex items-center justify-center gap-1.5">
                    <Zap className="h-4 w-4 shrink-0 fill-current" data-testid="cta-instant-zap" />
                    {t("booking.book_now")}
                  </span>
                ) : (
                  t("booking.request_to_book")
                )}
              </button>
              {carInstant && quote?.instantBooking && (
                <p className="mt-2 flex items-center justify-center gap-1 text-center text-xs font-medium text-brand" data-testid="text-instant-confirm-note">
                  <Zap className="h-3.5 w-3.5 shrink-0 fill-current" />
                  {t("booking.instant_confirm_note")}
                </p>
              )}
              {carInstant && selectedDatesMode && quote && !quote.instantBooking && (
                <p className="mt-2 text-center text-xs text-muted-foreground" data-testid="text-instant-dates-not-qualify">
                  {t("booking.instant_dates_not_qualify")}
                </p>
              )}
              {carInstant && selectedDatesMode && !quote && (
                <p className="mt-2 text-center text-xs text-muted-foreground" data-testid="text-instant-selected-dates-hint">
                  {t("booking.instant_selected_dates_hint")}
                </p>
              )}
              <p className="mt-2 text-center text-xs text-muted-foreground">{t("booking.wont_be_charged_yet")}</p>
            </>
          );
        })()
      )}


      </div>

      {isInsurable && (
        <InsuranceModal
          open={insuranceModalOpen}
          onOpenChange={(open) => {
            setInsuranceModalOpen(open);
          }}
          carId={car.id}
          rentalType={activeTab}
          selectedPackageId={selectedInsurancePackageId}
          onSelectPackage={(pkgId) => {
            setSelectedInsurancePackageId(pkgId);
            const confirmation = { context: coverageContext, packageId: pkgId };
            setWidgetConfirmation(confirmation);
            writeSecureCheckoutState(checkoutFlowIdRef.current, {
              insurancePackageId: pkgId, coverageConfirmation: confirmation,
            }, user?.id, canClaimCheckoutFlowAfterAuth(checkoutFlowIdRef.current));
            retainListingFlow();
            window.dispatchEvent(new Event("checkout-coverage-committed"));
            setInsuranceModalOpen(false);
            // Selection is committed before the modal closes. Do not let a
            // quick close/reopen outrun the general input-save debounce.
            if (mobile && !isPreview) writeBwSession(car.id, { ...readBwSession(car.id), insurancePackageId: pkgId });
          }}
          currency={currency}
        />
      )}

      {/* Mobile-bug fix #1: in-widget date Drawer. Renders only when the
          parent has supplied availability data + a date setter. The Drawer's
          z-index is higher than the booking bottom sheet so the calendar
          appears on top, and the Drawer auto-closes once both `from` and
          `to` are picked. */}
      {canOpenDateDrawer && (
        <Drawer open={dateDrawerOpen} onOpenChange={setDateDrawerOpen}>
          <DrawerContent className="flex max-h-[88vh] flex-col">
            <DrawerTitle className="px-4 pt-4 text-base font-semibold">
              {t("booking.select_dates", "Select dates")}
            </DrawerTitle>
            <div className="overflow-y-auto px-4 pb-6 pt-3">
              <AvailabilityCalendar
                availability={availability!}
                selectedFrom={dateFrom || externalFrom || null}
                selectedTo={dateTo || externalTo || null}
                rentalType={activeTab}
                onDateSelect={(from, to) => {
                  onDateSelect?.(from, to);
                  if (from) trackBookingBox("booking_date_selected", car.id, activeTab);
                  if (from && to) setDateDrawerOpen(false);
                }}
              />
            </div>
          </DrawerContent>
        </Drawer>
      )}
    </div>
  );
}
